import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Crosshair,
  List,
  Loader2,
  Map as MapIcon,
  Navigation,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Play,
  Plus,
  Route,
  Save,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

import { MainLayout } from '../components/layout/MainLayout';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TravelCalibrationPanel } from '../components/travel/TravelCalibrationPanel';
import { TravelMapCanvas } from '../components/travel/TravelMapCanvas';
import { TravelLayerSwitcher, TravelMapFeaturesPanel } from '../components/travel/TravelMapFeaturesPanel';
import { TravelRoutePanel } from '../components/travel/TravelRoutePanel';
import { TravelEnginePanel } from '../components/travel/TravelEnginePanel';
import { TravelRunCommandCenter } from '../components/travel/TravelRunCommandCenter';
import { TravelTerrainPanel } from '../components/travel/TravelTerrainPanel';
import {
  TravelJourneyLibrary,
  TravelLayersPopover,
  TravelPlacesPanel,
  TravelSetupRail,
} from '../components/travel/TravelWorkspaceChrome';
import {
  centerSpacingPx,
  formatHexId,
  getMapAxialBounds,
  parseHexKey,
} from '../components/travel/hexGridMath';
import { assessRouteLegs, planRouteVariants } from '../components/travel/routeEngine';
import { generateTravelRun } from '../components/travel/travelEngine';
import { networkEdgeKey, networkTypeKey, parseNetworkTypeKey } from '../components/travel/mapFeatures';
import { analyzeTerrainColors } from '../components/travel/terrainAnalysis';
import {
  classifyTerrainColors,
  countSampledTerrainTypes,
  isAmbiguousAssignment,
} from '../components/travel/terrainClassification';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { isGameMaster } from '../lib/utils';

const API_URL = '/api';

const copy = {
  fr: {
    title: 'Voyage',
    subtitle: 'Préparer et conduire les déplacements du groupe sans surcharger la carte de campagne.',
    universe: 'Univers de voyage',
    map: 'Carte monde active',
    units: 'Unités',
    save: 'Enregistrer la configuration',
    saved: 'Configuration du voyage enregistrée',
    loading: 'Ouverture du registre de voyage…',
    loadError: 'Impossible de charger le module Voyage.',
    forbidden: 'Le registre de voyage est réservé au maître de jeu.',
    noMap: 'Aucune carte disponible',
    noMapHelp: 'Importez d’abord une carte de type monde ou région. Voyage la réutilisera sans modifier son interface.',
    openMaps: 'Ouvrir Cartes',
    mapReady: 'Carte de voyage',
    mapPlaceholder: 'Choisissez une carte pour établir le terrain de voyage.',
    noJourney: 'Aucun trajet actif',
    noJourneyHelp: 'La carte est prête pour le futur calcul des itinéraires.',
    groupState: 'État du groupe',
    packageDriven: 'Déclaré par le paquet actif',
    journal: 'Journal de trajet',
    journalHelp: 'Les étapes apparaîtront ici pendant la partie. Ce panneau restera séparé des outils de préparation de Cartes.',
    canonical: 'Base de calcul',
    perHex: 'par hexagone recommandé',
    timeUnit: 'unité de journal',
    builtin: 'Fourni',
    custom: 'Personnalisé',
    setup: 'Carte de voyage prête',
    setupHelp: 'La grille, le calage et l’échelle par pixel sont maintenant propres à cette carte.',
    pending: 'À venir dans le prochain jalon',
    pendingHelp: 'Calcul des variantes rapide, sûre et discrète à partir des terrains renseignés.',
    gridCalibrated: 'Grille calée à partir des deux centres.',
    calibrationError: 'Impossible de charger ou d’enregistrer le calage de la carte.',
    calibrationTab: 'Calage', terrainTab: 'Terrain', routeTab: 'Itinéraire', engineTab: 'En route', terrainError: "Impossible de charger ou d’enregistrer les terrains.",
    routeError: "Impossible de charger ou d’enregistrer le brouillon d’itinéraire.",
    analyzeFirst: "Analysez d’abord les couleurs de la carte.",
    analyzeError: "L’analyse de la carte a échoué.",
    tooManyHexes: 'La grille contient plus de 100 000 hexagones. Agrandissez-les avant l’analyse.',
    corsError: "L’hébergeur de l’image interdit l’analyse des pixels. Réimportez la carte dans l’application.",
    sampleAdded: 'Teinte ajoutée ; les propositions ont été recalculées.',
    firstSampleAdded: 'Première teinte apprise. Ajoutez un second type de terrain pour afficher les propositions.',
    analysisComplete: '{count} hexagones analysés. Choisissez un terrain, puis prélevez une zone représentative.',
    hideTools: 'Replier les outils',
    showTools: 'Afficher les outils',
    pinsError: 'Impossible de charger les pins de cette carte.',
    featuresError: 'Impossible de charger ou d’enregistrer les réseaux et obstacles.',
    runError: "Impossible de charger ou d’enregistrer le voyage actif.",
    rerollWarning: "Cet événement modifie la suite du voyage. Confirmer son remplacement et recalculer les étapes provisoires ?",
  },
  en: {
    title: 'Travel',
    subtitle: 'Prepare and run party movement without overloading the campaign map.',
    universe: 'Travel universe',
    map: 'Active world map',
    units: 'Units',
    save: 'Save configuration',
    saved: 'Travel configuration saved',
    loading: 'Opening the travel register…',
    loadError: 'The Travel module could not be loaded.',
    forbidden: 'The travel register is reserved for the game master.',
    noMap: 'No map available',
    noMapHelp: 'Import a world or region map first. Travel will reuse it without changing the Maps interface.',
    openMaps: 'Open Maps',
    mapReady: 'Travel map',
    mapPlaceholder: 'Choose a map to establish the travel terrain.',
    noJourney: 'No active journey',
    noJourneyHelp: 'The map is ready for future route calculation.',
    groupState: 'Party state',
    packageDriven: 'Declared by the active package',
    journal: 'Travel log',
    journalHelp: 'Steps will appear here during play. This panel remains separate from the preparation tools in Maps.',
    canonical: 'Calculation basis',
    perHex: 'recommended per hex',
    timeUnit: 'log unit',
    builtin: 'Built-in',
    custom: 'Custom',
    setup: 'Travel map ready',
    setupHelp: 'Grid, alignment and pixel-based scale are now stored for this map.',
    pending: 'Coming in the next milestone',
    pendingHelp: 'Fastest, safest and stealthiest route variants computed from classified terrain.',
    gridCalibrated: 'Grid aligned from the two centers.',
    calibrationError: 'The map calibration could not be loaded or saved.',
    calibrationTab: 'Alignment', terrainTab: 'Terrain', routeTab: 'Route', engineTab: 'On the road', terrainError: 'Terrain data could not be loaded or saved.',
    routeError: 'The route draft could not be loaded or saved.',
    analyzeFirst: 'Analyze the map colors first.', analyzeError: 'Map analysis failed.',
    tooManyHexes: 'The grid contains more than 100,000 hexes. Enlarge them before analysis.',
    corsError: 'The image host blocks pixel analysis. Import the map into the application again.',
    sampleAdded: 'Color sampled; suggestions were recomputed.',
    firstSampleAdded: 'First color learned. Add a second terrain type to display proposals.',
    analysisComplete: '{count} hexes analyzed. Choose a terrain, then sample a representative area.',
    hideTools: 'Collapse tools',
    showTools: 'Show tools',
    pinsError: 'Pins for this map could not be loaded.',
    featuresError: 'Networks and obstacles could not be loaded or saved.',
    runError: 'The active journey could not be loaded or saved.',
    rerollWarning: 'This event changes the rest of the journey. Confirm replacement and recompute provisional steps?',
  },
};

const terrainGeometrySignature = (calibration) => calibration ? JSON.stringify({
  imageWidth: calibration.imageWidth,
  imageHeight: calibration.imageHeight,
  sizePx: calibration.grid.sizePx,
  orientation: calibration.grid.orientation,
  offsetX: calibration.grid.offsetX,
  offsetY: calibration.grid.offsetY,
  rotationDeg: calibration.grid.rotationDeg,
}) : '';

const shortHash = (value) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const travelRunSourceSignature = (routeDraft, terrainClassification, mapFeatures) => {
  const selected = routeDraft?.variants?.find((item) => item.profileId === routeDraft.selectedProfileId);
  if (!selected?.path?.length || !terrainClassification || !mapFeatures) return '';
  const path = selected.path;
  return shortHash(JSON.stringify({
    profileId: selected.profileId,
    path,
    durationHours: selected.durationHours,
    distanceMeters: selected.distanceMeters,
    transportId: routeDraft.transportId,
    paceId: routeDraft.paceId,
    dangerLevelId: routeDraft.dangerLevelId,
    terrain: path.map((key) => [key, terrainClassification.assignments?.[key] || null]),
    conditions: path.map((key) => [key, mapFeatures.conditions?.[key] || []]),
    networks: path.slice(1).map((key, index) => (
      mapFeatures.networkSegments?.[networkEdgeKey(path[index], key)] || null
    )),
  }));
};

export default function TravelPage({ surface = 'travel' }) {
  const { campaignId, mapId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, language, distanceUnit, switchDistanceUnit } = useAuth();
  const { currentCampaign, fetchCampaign } = useCampaign();
  const text = copy[language] || copy.fr;

  const [packs, setPacks] = useState([]);
  const [maps, setMaps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [selectedPackKey, setSelectedPackKey] = useState('');
  const [selectedMapId, setSelectedMapId] = useState('');
  const [packContent, setPackContent] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);
  const [calibrationSaveStatus, setCalibrationSaveStatus] = useState('saved');
  const [workspacePanel, setWorkspacePanel] = useState(surface === 'map-setup' ? 'terrain' : 'route');
  const [journeyView, setJourneyView] = useState(() => searchParams.get('view') || 'list');
  const [toolsPanelCollapsed, setToolsPanelCollapsed] = useState(false);
  const [interactionMode, setInteractionMode] = useState('navigate');
  const [measuredSegment, setMeasuredSegment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [terrainClassification, setTerrainClassification] = useState(null);
  const [terrainColors, setTerrainColors] = useState({});
  const [terrainLoading, setTerrainLoading] = useState(false);
  const [terrainStale, setTerrainStale] = useState(false);
  const [terrainSaveStatus, setTerrainSaveStatus] = useState('saved');
  const [analysisStatus, setAnalysisStatus] = useState('idle');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [selectedTerrainId, setSelectedTerrainId] = useState('');
  const [brushRadius, setBrushRadius] = useState(0);
  const [selectedTerrainHexes, setSelectedTerrainHexes] = useState(() => new Set());
  const [terrainUndoStack, setTerrainUndoStack] = useState([]);
  const [terrainRedoStack, setTerrainRedoStack] = useState([]);
  const [travelEditLayer, setTravelEditLayer] = useState('ground');
  const [mapFeatures, setMapFeatures] = useState(null);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresStale, setFeaturesStale] = useState(false);
  const [featuresSaveStatus, setFeaturesSaveStatus] = useState('saved');
  const [selectedNetworkType, setSelectedNetworkType] = useState('');
  const [selectedConditionId, setSelectedConditionId] = useState('');
  const [featureUndoStack, setFeatureUndoStack] = useState([]);
  const [featureRedoStack, setFeatureRedoStack] = useState([]);
  const [routeDraft, setRouteDraft] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeSaveStatus, setRouteSaveStatus] = useState('saved');
  const [routeStale, setRouteStale] = useState(false);
  const [routeComputing, setRouteComputing] = useState(false);
  const [travelRun, setTravelRun] = useState(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runSaveStatus, setRunSaveStatus] = useState('saved');
  const [runStale, setRunStale] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [animationPlaying, setAnimationPlaying] = useState(false);
  const [pins, setPins] = useState([]);
  const [pinsVisible, setPinsVisible] = useState(true);
  const [terrainOverlayOpacity, setTerrainOverlayOpacity] = useState(34);
  const initialRequestedMapIdRef = useRef(mapId || searchParams.get('mapId') || '');
  const lastSavedCalibrationRef = useRef('');
  const calibrationSaveSequenceRef = useRef(0);
  const calibrationMapIdRef = useRef(null);
  const lastSavedTerrainRef = useRef('');
  const terrainSaveSequenceRef = useRef(0);
  const terrainIdentityRef = useRef('');
  const terrainGeometryRef = useRef('');
  const terrainClassificationRef = useRef(null);
  const terrainStrokeStartRef = useRef(null);
  const lastSavedFeaturesRef = useRef('');
  const featuresSaveSequenceRef = useRef(0);
  const featuresIdentityRef = useRef('');
  const featuresGeometryRef = useRef('');
  const mapFeaturesRef = useRef(null);
  const featureStrokeStartRef = useRef(null);
  const lastSavedRouteRef = useRef('');
  const routeSaveSequenceRef = useRef(0);
  const routeIdentityRef = useRef('');
  const routeGeometryRef = useRef('');
  const lastSavedRunRef = useRef('');
  const runSaveSequenceRef = useRef(0);
  const runIdentityRef = useRef('');

  const memberRole = currentCampaign?.members?.find((member) => member.user_id === user?.id)?.role;
  const isMJ = isGameMaster(memberRole);
  const requestedMapId = mapId || initialRequestedMapIdRef.current;

  useEffect(() => {
    if (!currentCampaign || currentCampaign.id !== campaignId || !memberRole) return;
    if (!isMJ) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [packsResponse, mapsResponse, settingsResponse, journeysResponse] = await Promise.all([
          axios.get(`${API_URL}/travel/universe-packs`, { params: { locale: language, campaign_id: campaignId } }),
          axios.get(`${API_URL}/campaigns/${campaignId}/maps`),
          axios.get(`${API_URL}/campaigns/${campaignId}/travel/settings`),
          axios.get(`${API_URL}/campaigns/${campaignId}/travel/journeys`, { params: { locale: language } }),
        ]);
        if (cancelled) return;
        const availablePacks = packsResponse.data;
        const availableMaps = mapsResponse.data;
        const travelSettings = settingsResponse.data;
        setPacks(availablePacks);
        setMaps(availableMaps);
        setSettings(travelSettings);
        setJourneys(journeysResponse.data || []);

        const storedPack = travelSettings.universe
          ? `${travelSettings.universe.package_id}@${travelSettings.universe.version}`
          : '';
        const defaultPack = availablePacks
          .filter((item) => item.packageId === 'wyrdmaker.medieval')
          .sort((first, second) => second.version.localeCompare(first.version, undefined, { numeric: true }))[0]
          || availablePacks[0];
        setSelectedPackKey(storedPack || defaultPack?.id || '');

        const defaultMap = availableMaps.find((item) => item.map_type === 'world')
          || availableMaps.find((item) => item.map_type === 'region')
          || availableMaps[0];
        const requestedMap = availableMaps.find((item) => item.id === requestedMapId);
        setSelectedMapId(requestedMap?.id || travelSettings.activeMapId || defaultMap?.id || '');
      } catch (loadError) {
        if (!cancelled) setError(loadError.response?.data?.detail || text.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [campaignId, currentCampaign, isMJ, language, memberRole, requestedMapId, text.loadError]);

  const selectedPack = useMemo(
    () => packs.find((item) => item.id === selectedPackKey),
    [packs, selectedPackKey],
  );
  const activeMap = useMemo(
    () => maps.find((item) => item.id === selectedMapId),
    [maps, selectedMapId],
  );

  useEffect(() => {
    if (!activeMap) {
      setPins([]);
      return undefined;
    }
    let cancelled = false;
    setPins([]);
    axios.get(`${API_URL}/maps/${activeMap.id}/pins`)
      .then((response) => {
        if (!cancelled) setPins(response.data);
      })
      .catch(() => {
        if (!cancelled) {
          setPins([]);
          toast.error(text.pinsError);
        }
      });
    return () => { cancelled = true; };
  }, [activeMap, text.pinsError]);

  useEffect(() => {
    if (!selectedPack) {
      setPackContent(null);
      return;
    }
    let cancelled = false;
    setPackContent(null);
    axios.get(
      `${API_URL}/travel/universe-packs/${encodeURIComponent(selectedPack.packageId)}/versions/${encodeURIComponent(selectedPack.version)}`,
    ).then((response) => {
      if (!cancelled) setPackContent(response.data);
    }).catch(() => {
      if (!cancelled) setPackContent(null);
    });
    return () => { cancelled = true; };
  }, [selectedPack]);

  useEffect(() => {
    const terrains = packContent?.terrains?.bases || [];
    if (!terrains.some((terrain) => terrain.id === selectedTerrainId)) {
      setSelectedTerrainId(terrains[0]?.id || '');
    }
  }, [packContent, selectedTerrainId]);

  useEffect(() => {
    const infrastructure = packContent?.terrains?.infrastructure || [];
    const connections = packContent?.connections || [];
    const availableNetworkTypes = [
      ...infrastructure.map((item) => networkTypeKey('infrastructure', item.id)),
      ...connections.map((item) => networkTypeKey('connection', item.id)),
    ];
    if (!availableNetworkTypes.includes(selectedNetworkType)) {
      setSelectedNetworkType(availableNetworkTypes[0] || '');
    }
    const conditions = packContent?.terrains?.conditions || [];
    if (!conditions.some((condition) => condition.id === selectedConditionId)) {
      setSelectedConditionId(conditions[0]?.id || '');
    }
  }, [packContent, selectedConditionId, selectedNetworkType]);

  useEffect(() => {
    if (!activeMap) {
      setCalibration(null);
      return;
    }
    let cancelled = false;
    calibrationMapIdRef.current = null;
    calibrationSaveSequenceRef.current += 1;
    lastSavedCalibrationRef.current = '';
    setCalibration(null);
    setCalibrationLoading(true);
    setInteractionMode('navigate');
    setMeasuredSegment(null);
    axios.get(`${API_URL}/maps/${activeMap.id}/travel-calibration`)
      .then((response) => {
        if (cancelled) return;
        const serialized = JSON.stringify(response.data);
        calibrationMapIdRef.current = activeMap.id;
        lastSavedCalibrationRef.current = serialized;
        setCalibration(response.data);
        setCalibrationSaveStatus('saved');
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.response?.data?.detail || text.calibrationError);
      })
      .finally(() => {
        if (!cancelled) setCalibrationLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeMap, text.calibrationError]);

  useEffect(() => {
    if (!activeMap || !selectedPack || calibrationLoading || !calibration
      || calibrationMapIdRef.current !== activeMap.id) {
      if (!activeMap || !selectedPack) setTerrainClassification(null);
      return undefined;
    }
    let cancelled = false;
    const identity = `${activeMap.id}:${selectedPack.packageId}@${selectedPack.version}`;
    terrainSaveSequenceRef.current += 1;
    terrainIdentityRef.current = '';
    lastSavedTerrainRef.current = '';
    terrainClassificationRef.current = null;
    terrainGeometryRef.current = terrainGeometrySignature(calibration);
    setTerrainClassification(null);
    setTerrainColors({});
    setTerrainLoading(true);
    setTerrainStale(false);
    setAnalysisStatus('idle');
    setSelectedTerrainHexes(new Set());
    setTerrainUndoStack([]);
    setTerrainRedoStack([]);
    axios.get(`${API_URL}/maps/${activeMap.id}/travel-terrain`, {
      params: { package_id: selectedPack.packageId, version: selectedPack.version },
    }).then((response) => {
      if (cancelled) return;
      const serverContent = response.data.content;
      const content = response.data.stale
        ? {
          ...serverContent,
          geometryFingerprint: response.data.currentGeometryFingerprint,
          assignments: {},
        }
        : serverContent;
      terrainIdentityRef.current = identity;
      // Keep the obsolete server document recoverable until the MJ actually
      // reclassifies or paints against the new grid.
      lastSavedTerrainRef.current = response.data.stale ? content : serverContent;
      terrainClassificationRef.current = content;
      setTerrainClassification(content);
      setTerrainStale(response.data.stale);
      setTerrainSaveStatus(response.data.stale ? 'dirty' : 'saved');
    }).catch((loadError) => {
      if (!cancelled) toast.error(loadError.response?.data?.detail || text.terrainError);
    }).finally(() => {
      if (!cancelled) setTerrainLoading(false);
    });
    return () => { cancelled = true; };
    // Calibration edits are handled by the geometry invalidation effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMap, selectedPack, calibrationLoading, text.terrainError]);

  useEffect(() => {
    if (!activeMap || !selectedPack || calibrationLoading || !calibration
      || calibrationMapIdRef.current !== activeMap.id) {
      if (!activeMap || !selectedPack) setMapFeatures(null);
      return undefined;
    }
    let cancelled = false;
    const identity = `${activeMap.id}:${selectedPack.packageId}@${selectedPack.version}`;
    featuresSaveSequenceRef.current += 1;
    featuresIdentityRef.current = '';
    lastSavedFeaturesRef.current = '';
    mapFeaturesRef.current = null;
    featuresGeometryRef.current = terrainGeometrySignature(calibration);
    setMapFeatures(null);
    setFeaturesLoading(true);
    setFeaturesStale(false);
    setFeatureUndoStack([]);
    setFeatureRedoStack([]);
    axios.get(`${API_URL}/maps/${activeMap.id}/travel-features`, {
      params: { package_id: selectedPack.packageId, version: selectedPack.version },
    }).then((response) => {
      if (cancelled) return;
      const serverContent = response.data.content;
      const content = response.data.stale ? {
        ...serverContent,
        geometryFingerprint: response.data.currentGeometryFingerprint,
        networkSegments: {},
        conditions: {},
      } : serverContent;
      featuresIdentityRef.current = identity;
      lastSavedFeaturesRef.current = JSON.stringify(content);
      mapFeaturesRef.current = content;
      setMapFeatures(content);
      setFeaturesStale(response.data.stale);
      setFeaturesSaveStatus(response.data.stale ? 'dirty' : 'saved');
    }).catch((loadError) => {
      if (!cancelled) toast.error(loadError.response?.data?.detail || text.featuresError);
    }).finally(() => {
      if (!cancelled) setFeaturesLoading(false);
    });
    return () => { cancelled = true; };
    // Geometry changes invalidate loaded features locally without re-fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMap, selectedPack, calibrationLoading, text.featuresError]);

  useEffect(() => {
    if (!activeMap || !selectedPack || calibrationLoading || !calibration
      || calibrationMapIdRef.current !== activeMap.id) {
      if (!activeMap || !selectedPack) setRouteDraft(null);
      return undefined;
    }
    let cancelled = false;
    const identity = `${campaignId}:${activeMap.id}:${selectedPack.packageId}@${selectedPack.version}`;
    routeSaveSequenceRef.current += 1;
    routeIdentityRef.current = '';
    lastSavedRouteRef.current = '';
    routeGeometryRef.current = terrainGeometrySignature(calibration);
    setRouteDraft(null);
    setRouteLoading(true);
    setRouteStale(false);
    axios.get(`${API_URL}/campaigns/${campaignId}/travel/route-draft`, {
      params: {
        map_id: activeMap.id,
        package_id: selectedPack.packageId,
        version: selectedPack.version,
      },
    }).then((response) => {
      if (cancelled) return;
      const serverContent = response.data.content;
      const content = response.data.stale ? {
        ...serverContent,
        geometryFingerprint: response.data.currentGeometryFingerprint,
        startHexKey: null,
        destinationHexKey: null,
        waypointHexKeys: [],
        avoidedHexKeys: [],
        startPinId: null,
        destinationPinId: null,
        waypointPinIds: [],
        selectedProfileId: null,
        status: 'planning',
        variants: [],
      } : serverContent;
      routeIdentityRef.current = identity;
      lastSavedRouteRef.current = JSON.stringify(content);
      setRouteDraft(content);
      setRouteStale(response.data.stale);
      setRouteSaveStatus(response.data.stale ? 'dirty' : 'saved');
    }).catch((loadError) => {
      if (!cancelled) toast.error(loadError.response?.data?.detail || text.routeError);
    }).finally(() => {
      if (!cancelled) setRouteLoading(false);
    });
    return () => { cancelled = true; };
    // Geometry changes invalidate the loaded draft locally without re-fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMap, campaignId, selectedPack, calibrationLoading, text.routeError]);

  useEffect(() => {
    if (!activeMap || !selectedPack || calibrationLoading || !calibration
      || calibrationMapIdRef.current !== activeMap.id) {
      if (!activeMap || !selectedPack) setTravelRun(null);
      return undefined;
    }
    let cancelled = false;
    const identity = `${campaignId}:${activeMap.id}:${selectedPack.packageId}@${selectedPack.version}`;
    runSaveSequenceRef.current += 1;
    runIdentityRef.current = '';
    lastSavedRunRef.current = '';
    setTravelRun(null);
    setRunLoading(true);
    setRunStale(false);
    setAnimationPlaying(false);
    setAnimationProgress(0);
    axios.get(`${API_URL}/campaigns/${campaignId}/travel/run`, {
      params: {
        map_id: activeMap.id,
        package_id: selectedPack.packageId,
        version: selectedPack.version,
      },
    }).then((response) => {
      if (cancelled) return;
      runIdentityRef.current = identity;
      lastSavedRunRef.current = response.data.content ? JSON.stringify(response.data.content) : '';
      setTravelRun(response.data.content);
      setRunStale(response.data.stale);
      setRunSaveStatus(response.data.stale ? 'stale' : 'saved');
    }).catch((loadError) => {
      if (!cancelled) toast.error(loadError.response?.data?.detail || text.runError);
    }).finally(() => {
      if (!cancelled) setRunLoading(false);
    });
    return () => { cancelled = true; };
    // Calibration edits mark the loaded run stale without re-fetching it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMap, campaignId, selectedPack, calibrationLoading, text.runError]);

  useEffect(() => {
    if (!activeMap || !calibration || calibrationMapIdRef.current !== activeMap.id) return undefined;
    const serialized = JSON.stringify(calibration);
    if (serialized === lastSavedCalibrationRef.current) return undefined;
    const sequence = ++calibrationSaveSequenceRef.current;
    setCalibrationSaveStatus('dirty');
    const timeoutId = window.setTimeout(async () => {
      setCalibrationSaveStatus('saving');
      try {
        const response = await axios.put(`${API_URL}/maps/${activeMap.id}/travel-calibration`, calibration);
        if (sequence !== calibrationSaveSequenceRef.current) return;
        lastSavedCalibrationRef.current = JSON.stringify(response.data);
        setCalibration(response.data);
        setCalibrationSaveStatus('saved');
      } catch (saveError) {
        if (sequence === calibrationSaveSequenceRef.current) {
          setCalibrationSaveStatus('dirty');
          toast.error(saveError.response?.data?.detail || text.calibrationError);
        }
      }
    }, 650);
    return () => window.clearTimeout(timeoutId);
  }, [activeMap, calibration, text.calibrationError]);

  useEffect(() => {
    if (!calibration || !terrainClassification || terrainLoading) return;
    const signature = terrainGeometrySignature(calibration);
    if (!terrainGeometryRef.current) {
      terrainGeometryRef.current = signature;
      return;
    }
    if (signature === terrainGeometryRef.current) return;
    terrainGeometryRef.current = signature;
    const next = { ...terrainClassificationRef.current, assignments: {} };
    terrainClassificationRef.current = next;
    // A geometry edit invalidates the overlay but must not immediately erase
    // the last valid classification persisted on the server.
    lastSavedTerrainRef.current = next;
    setTerrainClassification(next);
    setTerrainColors({});
    setAnalysisStatus('idle');
    setAnalysisProgress(0);
    setTerrainStale(true);
    setTerrainSaveStatus('dirty');
    setSelectedTerrainHexes(new Set());
    setTerrainUndoStack([]);
    setTerrainRedoStack([]);
  }, [calibration, terrainClassification, terrainLoading]);

  useEffect(() => {
    if (!calibration || !mapFeatures || featuresLoading) return;
    const signature = terrainGeometrySignature(calibration);
    if (!featuresGeometryRef.current) {
      featuresGeometryRef.current = signature;
      return;
    }
    if (signature === featuresGeometryRef.current) return;
    featuresGeometryRef.current = signature;
    const next = { ...mapFeaturesRef.current, networkSegments: {}, conditions: {} };
    mapFeaturesRef.current = next;
    setMapFeatures(next);
    lastSavedFeaturesRef.current = JSON.stringify(next);
    setFeaturesStale(true);
    setFeaturesSaveStatus('dirty');
    setFeatureUndoStack([]);
    setFeatureRedoStack([]);
    setInteractionMode('navigate');
  }, [calibration, featuresLoading, mapFeatures]);

  useEffect(() => {
    if (!calibration || !routeDraft || routeLoading) return;
    const signature = terrainGeometrySignature(calibration);
    if (!routeGeometryRef.current) {
      routeGeometryRef.current = signature;
      return;
    }
    if (signature === routeGeometryRef.current) return;
    routeGeometryRef.current = signature;
    const next = {
      ...routeDraft,
      startHexKey: null,
      destinationHexKey: null,
      waypointHexKeys: [],
      avoidedHexKeys: [],
      startPinId: null,
      destinationPinId: null,
      waypointPinIds: [],
      selectedProfileId: null,
      status: 'planning',
      variants: [],
    };
    setRouteDraft(next);
    lastSavedRouteRef.current = JSON.stringify(next);
    setRouteStale(true);
    setRouteSaveStatus('dirty');
    setInteractionMode('navigate');
  }, [calibration, routeDraft, routeLoading]);

  useEffect(() => {
    if (!activeMap || !selectedPack || !terrainClassification || terrainLoading
      || calibrationSaveStatus !== 'saved') return undefined;
    const identity = `${activeMap.id}:${selectedPack.packageId}@${selectedPack.version}`;
    if (identity !== terrainIdentityRef.current) return undefined;
    if (terrainClassification === lastSavedTerrainRef.current) return undefined;
    const sequence = ++terrainSaveSequenceRef.current;
    setTerrainSaveStatus('dirty');
    const timeoutId = window.setTimeout(async () => {
      setTerrainSaveStatus('saving');
      try {
        const response = await axios.put(
          `${API_URL}/maps/${activeMap.id}/travel-terrain`,
          terrainClassification,
        );
        if (sequence !== terrainSaveSequenceRef.current) return;
        lastSavedTerrainRef.current = response.data;
        terrainClassificationRef.current = response.data;
        setTerrainClassification(response.data);
        setTerrainSaveStatus('saved');
      } catch (saveError) {
        if (sequence === terrainSaveSequenceRef.current) {
          setTerrainSaveStatus('dirty');
          toast.error(saveError.response?.data?.detail || text.terrainError);
        }
      }
    }, 800);
    return () => window.clearTimeout(timeoutId);
  }, [activeMap, calibrationSaveStatus, selectedPack, terrainClassification, terrainLoading, text.terrainError]);

  useEffect(() => {
    if (!activeMap || !selectedPack || !mapFeatures || featuresLoading
      || calibrationSaveStatus !== 'saved') return undefined;
    const identity = `${activeMap.id}:${selectedPack.packageId}@${selectedPack.version}`;
    if (identity !== featuresIdentityRef.current) return undefined;
    const serialized = JSON.stringify(mapFeatures);
    if (serialized === lastSavedFeaturesRef.current) return undefined;
    const sequence = ++featuresSaveSequenceRef.current;
    setFeaturesSaveStatus('dirty');
    const timeoutId = window.setTimeout(async () => {
      setFeaturesSaveStatus('saving');
      try {
        const response = await axios.put(`${API_URL}/maps/${activeMap.id}/travel-features`, mapFeatures);
        if (sequence !== featuresSaveSequenceRef.current) return;
        lastSavedFeaturesRef.current = JSON.stringify(response.data);
        mapFeaturesRef.current = response.data;
        setMapFeatures(response.data);
        setFeaturesSaveStatus('saved');
        setFeaturesStale(false);
      } catch (saveError) {
        if (sequence === featuresSaveSequenceRef.current) {
          setFeaturesSaveStatus('dirty');
          toast.error(saveError.response?.data?.detail || text.featuresError);
        }
      }
    }, 800);
    return () => window.clearTimeout(timeoutId);
  }, [activeMap, calibrationSaveStatus, featuresLoading, mapFeatures, selectedPack, text.featuresError]);

  useEffect(() => {
    if (!routeDraft || routeLoading || routeComputing || calibrationSaveStatus !== 'saved') return undefined;
    const identity = `${campaignId}:${routeDraft.mapId}:${routeDraft.packageId}@${routeDraft.packageVersion}`;
    if (identity !== routeIdentityRef.current) return undefined;
    const serialized = JSON.stringify(routeDraft);
    if (serialized === lastSavedRouteRef.current) return undefined;
    const sequence = ++routeSaveSequenceRef.current;
    setRouteSaveStatus('dirty');
    const timeoutId = window.setTimeout(async () => {
      setRouteSaveStatus('saving');
      try {
        const response = await axios.put(`${API_URL}/campaigns/${campaignId}/travel/route-draft`, routeDraft);
        if (sequence !== routeSaveSequenceRef.current) return;
        lastSavedRouteRef.current = JSON.stringify(response.data);
        setRouteDraft(response.data);
        setRouteSaveStatus('saved');
        if (response.data.startHexKey || response.data.destinationHexKey) setRouteStale(false);
      } catch (saveError) {
        if (sequence === routeSaveSequenceRef.current) {
          setRouteSaveStatus('dirty');
          toast.error(saveError.response?.data?.detail || text.routeError);
        }
      }
    }, 850);
    return () => window.clearTimeout(timeoutId);
  }, [calibrationSaveStatus, campaignId, routeComputing, routeDraft, routeLoading, text.routeError]);

  useEffect(() => {
    if (!travelRun || runLoading || runStale || calibrationSaveStatus !== 'saved') return undefined;
    const identity = `${campaignId}:${travelRun.mapId}:${travelRun.packageId}@${travelRun.packageVersion}`;
    if (identity !== runIdentityRef.current) return undefined;
    const serialized = JSON.stringify(travelRun);
    if (serialized === lastSavedRunRef.current) return undefined;
    const sequence = ++runSaveSequenceRef.current;
    setRunSaveStatus('dirty');
    const timeoutId = window.setTimeout(async () => {
      setRunSaveStatus('saving');
      try {
        const response = await axios.put(`${API_URL}/campaigns/${campaignId}/travel/run`, travelRun);
        if (sequence !== runSaveSequenceRef.current) return;
        lastSavedRunRef.current = JSON.stringify(response.data);
        setTravelRun(response.data);
        setRunSaveStatus('saved');
      } catch (saveError) {
        if (sequence === runSaveSequenceRef.current) {
          setRunSaveStatus('dirty');
          toast.error(saveError.response?.data?.detail?.message || saveError.response?.data?.detail || text.runError);
        }
      }
    }, 900);
    return () => window.clearTimeout(timeoutId);
  }, [calibrationSaveStatus, campaignId, runLoading, runStale, text.runError, travelRun]);

  useEffect(() => {
    if (!animationPlaying) return undefined;
    const intervalId = window.setInterval(() => {
      setAnimationProgress((current) => {
        const next = Math.min(1, current + 0.0065);
        if (next >= 1) setAnimationPlaying(false);
        return next;
      });
    }, 50);
    return () => window.clearInterval(intervalId);
  }, [animationPlaying]);

  useEffect(() => {
    const recommended = packContent?.distance?.recommendedMetersPerHex?.default;
    if (!calibration || !recommended || calibration.scale.metersPerPixel) return;
    setCalibration((current) => ({
      ...current,
      scale: {
        ...current.scale,
        metersPerPixel: recommended / centerSpacingPx(current.grid),
        calibrationMethod: 'universe_default',
        referencePixelLength: centerSpacingPx(current.grid),
        referenceDistanceMeters: recommended,
      },
    }));
  }, [calibration, packContent]);

  useEffect(() => {
    if (!routeDraft || !calibration || !packContent || routeLoading) return undefined;
    if (!routeDraft.startHexKey || !routeDraft.destinationHexKey || !calibration.scale.metersPerPixel) {
      setRouteComputing(false);
      if (routeDraft.variants.length) {
        setRouteDraft((current) => current ? { ...current, variants: [], selectedProfileId: null, status: 'planning' } : current);
      }
      return undefined;
    }
    setRouteComputing(true);
    const expected = {
      start: routeDraft.startHexKey,
      destination: routeDraft.destinationHexKey,
      waypoints: routeDraft.waypointHexKeys.join('|'),
      avoided: (routeDraft.avoidedHexKeys || []).join('|'),
      transport: routeDraft.transportId,
      pace: routeDraft.paceId,
      danger: routeDraft.dangerLevelId,
    };
    const timeoutId = window.setTimeout(() => {
      const transport = packContent.transports.find((item) => item.id === expected.transport);
      const pace = packContent.paces.find((item) => item.id === expected.pace);
      const dangerLevel = packContent.dangerLevels.find((item) => item.id === expected.danger);
      const variants = planRouteVariants({
        startKey: expected.start,
        destinationKey: expected.destination,
        waypointKeys: expected.waypoints ? expected.waypoints.split('|') : [],
        grid: calibration.grid,
        width: calibration.imageWidth,
        height: calibration.imageHeight,
        metersPerPixel: calibration.scale.metersPerPixel,
        terrains: packContent.terrains.bases,
        assignments: terrainClassification?.assignments || {},
        infrastructure: packContent.terrains.infrastructure || [],
        connections: packContent.connections || [],
        conditions: packContent.terrains.conditions || [],
        networkSegments: mapFeatures?.networkSegments || {},
        conditionAssignments: mapFeatures?.conditions || {},
        avoidedHexKeys: expected.avoided ? expected.avoided.split('|') : [],
        transport,
        pace,
        dangerLevel,
        profiles: packContent.routeProfiles,
      });
      setRouteDraft((current) => {
        if (!current || current.startHexKey !== expected.start
          || current.destinationHexKey !== expected.destination
          || current.waypointHexKeys.join('|') !== expected.waypoints
          || (current.avoidedHexKeys || []).join('|') !== expected.avoided
          || current.transportId !== expected.transport
          || current.paceId !== expected.pace
          || current.dangerLevelId !== expected.danger) return current;
        const selectedAvailable = variants.some(
          (variant) => variant.profileId === current.selectedProfileId && !variant.unavailable,
        );
        const selectedProfileId = selectedAvailable ? current.selectedProfileId : null;
        const previousSelected = current.variants.find((variant) => variant.profileId === selectedProfileId);
        const nextSelected = variants.find((variant) => variant.profileId === selectedProfileId);
        const selectedPathChanged = previousSelected && nextSelected
          ? previousSelected.path.join('|') !== nextSelected.path.join('|')
          : false;
        const status = current.status === 'confirmed' && !selectedPathChanged
          ? 'confirmed'
          : selectedProfileId ? 'ready' : 'planning';
        return { ...current, variants, selectedProfileId, status };
      });
      setRouteComputing(false);
    }, 140);
    return () => window.clearTimeout(timeoutId);
    // Only route inputs, calibration and terrain assignments should trigger a recomputation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    calibration,
    packContent,
    routeDraft?.dangerLevelId,
    routeDraft?.destinationHexKey,
    routeDraft?.paceId,
    routeDraft?.startHexKey,
    routeDraft?.transportId,
    routeDraft?.waypointHexKeys,
    routeDraft?.avoidedHexKeys,
    routeLoading,
    mapFeatures?.conditions,
    mapFeatures?.networkSegments,
    terrainClassification?.assignments,
  ]);

  useEffect(() => {
    if (!travelRun || !routeDraft || routeLoading || terrainLoading || featuresLoading) return;
    const selected = routeDraft.variants.find((item) => item.profileId === routeDraft.selectedProfileId);
    if (!selected?.path?.length) return;
    const sourceSignature = travelRunSourceSignature(routeDraft, terrainClassification, mapFeatures);
    if (selected.profileId !== travelRun.routeProfileId
      || selected.path.join('|') !== travelRun.routePath.join('|')
      || (sourceSignature && sourceSignature !== travelRun.sourceSignature)) {
      setRunStale(true);
      setRunSaveStatus('stale');
    }
  }, [featuresLoading, mapFeatures, routeDraft, routeLoading, terrainClassification, terrainLoading, travelRun]);

  const updateCalibration = useCallback((nextCalibration) => {
    setCalibration(nextCalibration);
  }, []);

  const updateGrid = useCallback((nextGrid) => {
    setCalibration((current) => current ? { ...current, grid: nextGrid } : current);
  }, []);

  const handleGridCalibrated = useCallback((result) => {
    setCalibration((current) => current ? {
      ...current,
      grid: { ...current.grid, ...result, enabled: true },
    } : current);
    toast.success(text.gridCalibrated);
  }, [text.gridCalibrated]);

  const handleImageDimensions = useCallback(({ width, height }) => {
    setCalibration((current) => current ? { ...current, imageWidth: width, imageHeight: height } : current);
  }, []);

  const updateTerrainClassification = useCallback((updater) => {
    const current = terrainClassificationRef.current;
    if (!current) return;
    const next = typeof updater === 'function' ? updater(current) : updater;
    terrainClassificationRef.current = next;
    setTerrainClassification(next);
  }, []);

  const recordTerrainChange = useCallback((previousAssignments) => {
    const currentAssignments = terrainClassificationRef.current?.assignments;
    if (!previousAssignments || previousAssignments === currentAssignments) return;
    setTerrainUndoStack((stack) => [...stack.slice(-29), previousAssignments]);
    setTerrainRedoStack([]);
  }, []);

  const replaceTerrainAssignments = useCallback((assignments) => {
    updateTerrainClassification((current) => ({ ...current, assignments }));
  }, [updateTerrainClassification]);

  const handleAnalyzeTerrain = useCallback(async () => {
    if (!activeMap || !calibration || !terrainClassificationRef.current) return;
    const analysisIdentity = terrainIdentityRef.current;
    const analysisGeometry = terrainGeometrySignature(calibration);
    setAnalysisStatus('running');
    setAnalysisProgress(0);
    try {
      const colors = await analyzeTerrainColors({
        imageUrl: activeMap.image_url,
        calibration,
        onProgress: setAnalysisProgress,
      });
      if (
        analysisIdentity !== terrainIdentityRef.current
        || analysisGeometry !== terrainGeometryRef.current
      ) return;
      setTerrainColors(colors);
      const current = terrainClassificationRef.current;
      const assignments = classifyTerrainColors(colors, current.samples, current.assignments);
      replaceTerrainAssignments(assignments);
      setTerrainUndoStack([]);
      setTerrainRedoStack([]);
      setAnalysisStatus('ready');
      setTerrainStale(false);
      setInteractionMode('terrain_sample');
      toast.success(text.analysisComplete.replace('{count}', Object.keys(colors).length.toLocaleString(language)));
    } catch (analysisError) {
      setAnalysisStatus('error');
      if (analysisError.message === 'too_many_hexes') toast.error(text.tooManyHexes);
      else if (analysisError.message === 'image_cors_blocked') toast.error(text.corsError);
      else toast.error(text.analyzeError);
    }
  }, [activeMap, calibration, language, replaceTerrainAssignments, text.analysisComplete, text.analyzeError, text.corsError, text.tooManyHexes]);

  const handleTerrainSample = useCallback((key) => {
    const descriptor = terrainColors[key];
    const current = terrainClassificationRef.current;
    if (!descriptor || !current) {
      toast.info(text.analyzeFirst);
      return;
    }
    if (!selectedTerrainId) return;
    const { features, r, g, b } = descriptor;
    const sample = {
      id: window.crypto?.randomUUID?.() || `sample-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      terrainId: selectedTerrainId,
      color: { r, g, b },
      ...(features ? { features } : {}),
      sourceHexKey: key,
    };
    const samples = [...current.samples, sample];
    const assignments = classifyTerrainColors(terrainColors, samples, current.assignments);
    updateTerrainClassification({ ...current, samples, assignments });
    setTerrainUndoStack([]);
    setTerrainRedoStack([]);
    toast.success(countSampledTerrainTypes(samples) < 2 ? text.firstSampleAdded : text.sampleAdded);
  }, [selectedTerrainId, terrainColors, text.analyzeFirst, text.firstSampleAdded, text.sampleAdded, updateTerrainClassification]);

  const handleRemoveTerrainSample = useCallback((sampleId) => {
    const current = terrainClassificationRef.current;
    if (!current) return;
    const previous = current.assignments;
    const samples = current.samples.filter((sample) => sample.id !== sampleId);
    const manualAssignments = Object.fromEntries(
      Object.entries(previous).filter(([, assignment]) => assignment.source === 'manual'),
    );
    const assignments = Object.keys(terrainColors).length
      ? classifyTerrainColors(terrainColors, samples, manualAssignments)
      : manualAssignments;
    updateTerrainClassification({ ...current, samples, assignments });
    setTerrainUndoStack([]);
    setTerrainRedoStack([]);
  }, [terrainColors, updateTerrainClassification]);

  const handleReclassifyTerrain = useCallback(() => {
    const current = terrainClassificationRef.current;
    if (!current || !Object.keys(terrainColors).length) {
      toast.info(text.analyzeFirst);
      return;
    }
    const previous = current.assignments;
    const assignments = classifyTerrainColors(terrainColors, current.samples, previous);
    replaceTerrainAssignments(assignments);
    setTerrainUndoStack([]);
    setTerrainRedoStack([]);
  }, [replaceTerrainAssignments, terrainColors, text.analyzeFirst]);

  const handleTerrainStrokeStart = useCallback(() => {
    terrainStrokeStartRef.current = terrainClassificationRef.current?.assignments || null;
  }, []);

  const handleTerrainPaint = useCallback((keys) => {
    if (!selectedTerrainId || !keys.length) return;
    updateTerrainClassification((current) => {
      let changed = false;
      const assignments = { ...current.assignments };
      keys.forEach((key) => {
        const existing = assignments[key];
        if (existing?.terrainId === selectedTerrainId && existing.source === 'manual') return;
        assignments[key] = { terrainId: selectedTerrainId, confidence: 1, source: 'manual' };
        changed = true;
      });
      return changed ? { ...current, assignments } : current;
    });
  }, [selectedTerrainId, updateTerrainClassification]);

  const handleTerrainStrokeEnd = useCallback(() => {
    recordTerrainChange(terrainStrokeStartRef.current);
    terrainStrokeStartRef.current = null;
  }, [recordTerrainChange]);

  const handleToggleTerrainHex = useCallback((key) => {
    setSelectedTerrainHexes((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleApplyTerrainSelection = useCallback(() => {
    if (!selectedTerrainId || !selectedTerrainHexes.size) return;
    const previous = terrainClassificationRef.current?.assignments;
    handleTerrainPaint([...selectedTerrainHexes]);
    recordTerrainChange(previous);
  }, [handleTerrainPaint, recordTerrainChange, selectedTerrainHexes, selectedTerrainId]);

  const handleTerrainUndo = useCallback(() => {
    setTerrainUndoStack((stack) => {
      if (!stack.length) return stack;
      const previous = stack[stack.length - 1];
      const current = terrainClassificationRef.current?.assignments || {};
      setTerrainRedoStack((redo) => [...redo.slice(-29), current]);
      replaceTerrainAssignments(previous);
      return stack.slice(0, -1);
    });
  }, [replaceTerrainAssignments]);

  const handleTerrainRedo = useCallback(() => {
    setTerrainRedoStack((stack) => {
      if (!stack.length) return stack;
      const next = stack[stack.length - 1];
      const current = terrainClassificationRef.current?.assignments || {};
      setTerrainUndoStack((undo) => [...undo.slice(-29), current]);
      replaceTerrainAssignments(next);
      return stack.slice(0, -1);
    });
  }, [replaceTerrainAssignments]);

  const updateMapFeatures = useCallback((updater) => {
    const current = mapFeaturesRef.current;
    if (!current) return;
    const next = typeof updater === 'function' ? updater(current) : updater;
    mapFeaturesRef.current = next;
    setMapFeatures(next);
  }, []);

  const featureSnapshot = useCallback((features = mapFeaturesRef.current) => ({
    networkSegments: features?.networkSegments || {},
    conditions: features?.conditions || {},
  }), []);

  const recordFeatureChange = useCallback((previous) => {
    if (!previous) return;
    const current = featureSnapshot();
    if (previous.networkSegments === current.networkSegments
      && previous.conditions === current.conditions) return;
    setFeatureUndoStack((stack) => [...stack.slice(-29), previous]);
    setFeatureRedoStack([]);
    setFeaturesStale(false);
  }, [featureSnapshot]);

  const handleNetworkStrokeStart = useCallback(() => {
    featureStrokeStartRef.current = featureSnapshot();
  }, [featureSnapshot]);

  const handleNetworkPaint = useCallback((edgeKeys, erase = false) => {
    if (!edgeKeys.length) return;
    const selected = parseNetworkTypeKey(selectedNetworkType);
    if (!erase && !selected) return;
    updateMapFeatures((current) => {
      let changed = false;
      const networkSegments = { ...current.networkSegments };
      edgeKeys.forEach((edgeKey) => {
        if (erase) {
          if (networkSegments[edgeKey]) {
            delete networkSegments[edgeKey];
            changed = true;
          }
          return;
        }
        const existing = networkSegments[edgeKey];
        if (existing?.catalog === selected.catalog && existing?.typeId === selected.typeId) return;
        networkSegments[edgeKey] = selected;
        changed = true;
      });
      return changed ? { ...current, networkSegments } : current;
    });
  }, [selectedNetworkType, updateMapFeatures]);

  const handleNetworkStrokeEnd = useCallback(() => {
    recordFeatureChange(featureStrokeStartRef.current);
    featureStrokeStartRef.current = null;
  }, [recordFeatureChange]);

  const handleConditionPaint = useCallback((key, erase = false) => {
    if (!erase && !selectedConditionId) return;
    const previous = featureSnapshot();
    updateMapFeatures((current) => {
      const conditions = { ...current.conditions };
      if (erase) {
        if (!conditions[key]) return current;
        delete conditions[key];
      } else {
        const existing = conditions[key] || [];
        if (existing.includes(selectedConditionId)) return current;
        conditions[key] = [...existing, selectedConditionId].slice(-8);
      }
      return { ...current, conditions };
    });
    recordFeatureChange(previous);
  }, [featureSnapshot, recordFeatureChange, selectedConditionId, updateMapFeatures]);

  const applyFeatureSnapshot = useCallback((snapshot) => {
    updateMapFeatures((current) => ({
      ...current,
      networkSegments: snapshot.networkSegments,
      conditions: snapshot.conditions,
    }));
  }, [updateMapFeatures]);

  const handleFeatureUndo = useCallback(() => {
    setFeatureUndoStack((stack) => {
      if (!stack.length) return stack;
      const previous = stack[stack.length - 1];
      setFeatureRedoStack((redo) => [...redo.slice(-29), featureSnapshot()]);
      applyFeatureSnapshot(previous);
      return stack.slice(0, -1);
    });
  }, [applyFeatureSnapshot, featureSnapshot]);

  const handleFeatureRedo = useCallback(() => {
    setFeatureRedoStack((stack) => {
      if (!stack.length) return stack;
      const next = stack[stack.length - 1];
      setFeatureUndoStack((undo) => [...undo.slice(-29), featureSnapshot()]);
      applyFeatureSnapshot(next);
      return stack.slice(0, -1);
    });
  }, [applyFeatureSnapshot, featureSnapshot]);

  const handleRouteDraftChange = useCallback((nextDraft) => {
    setRouteDraft(nextDraft);
    setRouteStale(false);
  }, []);

  const handleRouteHexPick = useCallback((mode, key, pinId = null) => {
    setRouteDraft((current) => {
      if (!current) return current;
      if (mode === 'route_start') {
        return { ...current, startHexKey: key, startPinId: pinId, avoidedHexKeys: [], status: 'planning', variants: [] };
      }
      if (mode === 'route_destination') {
        return { ...current, destinationHexKey: key, destinationPinId: pinId, avoidedHexKeys: [], status: 'planning', variants: [] };
      }
      if (mode === 'route_waypoint') {
        if (key === current.startHexKey || key === current.destinationHexKey || current.waypointHexKeys.includes(key)) return current;
        return {
          ...current,
          waypointHexKeys: [...current.waypointHexKeys, key],
          waypointPinIds: [...(current.waypointPinIds || current.waypointHexKeys.map(() => null)), pinId],
          status: 'planning',
          variants: [],
        };
      }
      if (mode === 'route_edit') {
        if (key === current.startHexKey || key === current.destinationHexKey) return current;
        const avoidedHexKeys = current.avoidedHexKeys || [];
        const selectedPath = current.variants.find(
          (variant) => variant.profileId === current.selectedProfileId,
        )?.path || [];
        if (!avoidedHexKeys.includes(key) && !selectedPath.includes(key)) return current;
        const nextAvoided = avoidedHexKeys.includes(key)
          ? avoidedHexKeys.filter((avoidedKey) => avoidedKey !== key)
          : [...avoidedHexKeys, key];
        return { ...current, avoidedHexKeys: nextAvoided, status: 'planning', variants: [] };
      }
      return current;
    });
    setRouteStale(false);
    if (mode === 'route_start' && !routeDraft?.destinationHexKey) setInteractionMode('route_destination');
    else if (mode !== 'route_edit') setInteractionMode('navigate');
  }, [routeDraft?.destinationHexKey]);

  const handleRemoveRouteWaypoint = useCallback((index) => {
    setRouteDraft((current) => current ? {
      ...current,
      waypointHexKeys: current.waypointHexKeys.filter((_, waypointIndex) => waypointIndex !== index),
      waypointPinIds: (current.waypointPinIds || current.waypointHexKeys.map(() => null))
        .filter((_, waypointIndex) => waypointIndex !== index),
      status: 'planning',
      variants: [],
    } : current);
  }, []);

  const handleRemoveAvoidedHex = useCallback((key) => {
    setRouteDraft((current) => current ? {
      ...current,
      avoidedHexKeys: (current.avoidedHexKeys || []).filter((avoidedKey) => avoidedKey !== key),
      status: 'planning',
      variants: [],
    } : current);
  }, []);

  const handleResetRoute = useCallback(() => {
    setRouteDraft((current) => current ? {
      ...current,
      startHexKey: null,
      destinationHexKey: null,
      waypointHexKeys: [],
      avoidedHexKeys: [],
      startPinId: null,
      destinationPinId: null,
      waypointPinIds: [],
      selectedProfileId: null,
      status: 'planning',
      variants: [],
    } : current);
    setInteractionMode('route_start');
  }, []);

  const handleAssignUnknownTerrain = useCallback((terrainId) => {
    const selectedVariant = routeDraft?.variants.find(
      (variant) => variant.profileId === routeDraft.selectedProfileId,
    );
    const key = selectedVariant?.unknownHexKeys.find(
      (hex) => !terrainClassificationRef.current?.assignments?.[hex],
    );
    const current = terrainClassificationRef.current;
    if (!key || !current) return;
    const previous = current.assignments;
    updateTerrainClassification({
      ...current,
      assignments: {
        ...previous,
        [key]: { terrainId, confidence: 1, source: 'manual' },
      },
    });
    recordTerrainChange(previous);
  }, [recordTerrainChange, routeDraft, updateTerrainClassification]);

  const handleConfirmRoute = useCallback(() => {
    setRouteDraft((current) => current?.selectedProfileId ? { ...current, status: 'confirmed' } : current);
    setWorkspacePanel('engine');
    setInteractionMode('navigate');
  }, []);

  const createRunSnapshot = useCallback((setup, slotOverrides = {}) => {
    if (!packContent || !routeDraft || !calibration || !selectedPack || !activeMap) return null;
    const selected = routeDraft.variants.find((item) => item.profileId === routeDraft.selectedProfileId);
    if (!selected?.path?.length || routeDraft.status !== 'confirmed') return null;
    const parameters = {
      ...setup.parameters,
      transportId: routeDraft.transportId,
      paceId: routeDraft.paceId,
      dangerLevelId: routeDraft.dangerLevelId,
    };
    const transport = packContent.transports.find((item) => item.id === parameters.transportId);
    const pace = packContent.paces.find((item) => item.id === parameters.paceId);
    const dangerLevel = packContent.dangerLevels.find((item) => item.id === parameters.dangerLevelId);
    const legs = assessRouteLegs({
      path: selected.path,
      grid: calibration.grid,
      metersPerPixel: calibration.scale.metersPerPixel,
      terrains: packContent.terrains.bases,
      assignments: terrainClassification?.assignments || {},
      infrastructure: packContent.terrains.infrastructure || [],
      connections: packContent.connections || [],
      conditions: packContent.terrains.conditions || [],
      networkSegments: mapFeatures?.networkSegments || {},
      conditionAssignments: mapFeatures?.conditions || {},
      transport,
      pace,
      dangerLevel,
    });
    const generated = generateTravelRun({
      pack: packContent,
      route: selected,
      legs,
      party: setup.party,
      parameters,
      initialState: setup.initialState,
      locale: language,
      slotOverrides,
    });
    if (!generated) return null;
    return {
      mapId: activeMap.id,
      packageId: selectedPack.packageId,
      packageVersion: selectedPack.version,
      packageChecksum: selectedPack.checksum,
      geometryFingerprint: routeDraft.geometryFingerprint,
      sourceSignature: travelRunSourceSignature(routeDraft, terrainClassification, mapFeatures),
      ...generated,
    };
  }, [activeMap, calibration, language, mapFeatures, packContent, routeDraft, selectedPack, terrainClassification]);

  const handleGenerateRun = useCallback(async (setup) => {
    let next = createRunSnapshot(setup);
    if (!next) return;
    if (travelRun && travelRun.routePath.join('|') === next.routePath.join('|')) {
      next = {
        ...next,
        steps: next.steps.map((step, index) => {
          const previous = travelRun.steps[index];
          if (!previous) return step;
          const locked = previous.lockedFields || [];
          return {
            ...step,
            ...Object.fromEntries(locked.map((field) => [field, previous[field]])),
            editedFields: previous.editedFields || {},
            lockedFields: locked,
          };
        }),
      };
    }
    try {
      const routeResponse = await axios.put(`${API_URL}/campaigns/${campaignId}/travel/route-draft`, routeDraft);
      lastSavedRouteRef.current = JSON.stringify(routeResponse.data);
      setRouteDraft(routeResponse.data);
      runIdentityRef.current = `${campaignId}:${next.mapId}:${next.packageId}@${next.packageVersion}`;
      setTravelRun(next);
      setRunStale(false);
      setRunSaveStatus('dirty');
      setAnimationProgress(0);
      setAnimationPlaying(true);
      setWorkspacePanel('engine');
      setJourneyView('run');
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('view', 'run');
      nextParams.set('mapId', next.mapId);
      setSearchParams(nextParams);
      setJourneys((current) => [{
        id: `run:${next.mapId}:${next.packageId}@${next.packageVersion}`,
        kind: 'run',
        status: next.status,
        mapId: next.mapId,
        mapName: activeMap?.name,
        packageId: next.packageId,
        packageVersion: next.packageVersion,
        packageName: selectedPack?.name,
        partyName: next.party?.name,
        seed: next.seed,
        currentStepIndex: next.currentStepIndex,
        stepCount: next.steps?.length || 0,
        generatedAt: next.generatedAt,
      }, ...current.filter((journey) => !(
        journey.mapId === next.mapId
        && journey.packageId === next.packageId
        && journey.packageVersion === next.packageVersion
      ))]);
    } catch (generationError) {
      toast.error(generationError.response?.data?.detail || text.runError);
    }
  }, [activeMap?.name, campaignId, createRunSnapshot, routeDraft, searchParams, selectedPack?.name, setSearchParams, text.runError, travelRun]);

  const handleRerollEvent = useCallback((stepIndex) => {
    if (!travelRun || stepIndex < travelRun.currentStepIndex) return;
    const slotOverrides = Object.fromEntries(travelRun.steps
      .filter((step) => step.slotSeeds?.event && step.slotSeeds.event !== travelRun.seed)
      .map((step) => [step.index, { event: step.slotSeeds.event }]));
    const currentEventSeed = travelRun.steps[stepIndex].slotSeeds?.event || travelRun.seed;
    slotOverrides[stepIndex] = { event: `${currentEventSeed}:reroll` };
    const candidate = createRunSnapshot({
      party: travelRun.party,
      parameters: travelRun.parameters,
      initialState: travelRun.initialState,
    }, slotOverrides);
    if (!candidate) return;
    const steps = candidate.steps.map((step, index) => {
      const previous = travelRun.steps[index];
      if (!previous) return step;
      const locked = index === stepIndex
        ? (previous.lockedFields || []).filter((field) => field !== 'eventNarration')
        : previous.lockedFields || [];
      const preserved = Object.fromEntries(locked.map((field) => [field, previous[field]]));
      return {
        ...step,
        ...preserved,
        editedFields: previous.editedFields || {},
        lockedFields: locked,
      };
    });
    if (stepIndex < travelRun.steps.length - 1 && !window.confirm(text.rerollWarning)) return;
    const currentState = travelRun.currentStepIndex === 0
      ? steps[0].stateBefore
      : steps[travelRun.currentStepIndex - 1].stateAfter;
    const activeThreads = travelRun.currentStepIndex === 0
      ? []
      : steps[travelRun.currentStepIndex - 1].activeThreadsAfter;
    setTravelRun({
      ...candidate,
      steps,
      currentStepIndex: travelRun.currentStepIndex,
      currentState,
      activeThreads,
      status: travelRun.status,
    });
    setRunStale(false);
  }, [createRunSnapshot, text.rerollWarning, travelRun]);

  const saveSettings = async () => {
    if (!selectedPack || !selectedMapId) return;
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/campaigns/${campaignId}/travel/settings`, {
        universePackId: selectedPack.packageId,
        universePackVersion: selectedPack.version,
        activeMapId: selectedMapId,
      });
      setSettings(response.data);
      await fetchCampaign(campaignId);
      toast.success(text.saved);
    } catch (saveError) {
      toast.error(saveError.response?.data?.detail || text.loadError);
    } finally {
      setSaving(false);
    }
  };

  const recommendedMeters = packContent?.distance?.recommendedMetersPerHex?.default;
  const accentColor = selectedPack?.accentColor || '#B8864B';
  const isConfigured = Boolean(settings?.universe && settings?.activeMapId);
  const terrains = useMemo(() => packContent?.terrains?.bases || [], [packContent]);
  const infrastructure = useMemo(() => packContent?.terrains?.infrastructure || [], [packContent]);
  const connections = useMemo(() => packContent?.connections || [], [packContent]);
  const terrainConditions = useMemo(() => packContent?.terrains?.conditions || [], [packContent]);
  const terrainPalette = useMemo(
    () => Object.fromEntries(terrains.map((terrain) => [terrain.id, terrain.referenceColors?.[0] || '#888888'])),
    [terrains],
  );
  const terrainStats = useMemo(() => {
    const assignments = Object.values(terrainClassification?.assignments || {});
    return {
      total: assignments.length,
      ambiguous: assignments.filter(isAmbiguousAssignment).length,
      manual: assignments.filter((assignment) => assignment.source === 'manual').length,
    };
  }, [terrainClassification]);
  const analyzedHexCount = useMemo(() => Object.keys(terrainColors).length, [terrainColors]);
  const routeMapBounds = useMemo(
    () => calibration ? getMapAxialBounds(calibration.grid, calibration.imageWidth, calibration.imageHeight) : null,
    [calibration],
  );
  const routeHexLabel = useCallback((key) => {
    if (!key || !routeMapBounds || !calibration) return '';
    const axial = parseHexKey(key);
    return formatHexId(axial.q, axial.r, routeMapBounds, calibration.grid.numbering);
  }, [calibration, routeMapBounds]);
  const pinById = useMemo(() => Object.fromEntries(pins.map((pin) => [pin.id, pin])), [pins]);
  const routePointLabel = useCallback((key, pinId) => {
    const pin = pinId && pinById[pinId];
    const hexLabel = routeHexLabel(key);
    return pin ? `${pin.title} · ${hexLabel}` : hexLabel;
  }, [pinById, routeHexLabel]);
  const selectedRouteVariant = useMemo(
    () => routeDraft?.variants?.find((variant) => variant.profileId === routeDraft.selectedProfileId),
    [routeDraft],
  );
  const pendingUnknownRouteHexes = useMemo(
    () => (selectedRouteVariant?.unknownHexKeys || []).filter(
      (key) => !terrainClassification?.assignments?.[key],
    ),
    [selectedRouteVariant, terrainClassification],
  );
  const routePoints = useMemo(() => routeDraft ? ({
    startHexKey: routeDraft.startHexKey,
    destinationHexKey: routeDraft.destinationHexKey,
    waypointHexKeys: routeDraft.waypointHexKeys,
  }) : null, [routeDraft]);
  const routeProfileColors = useMemo(() => {
    const colors = ['#F59E0B', '#10B981', '#8B5CF6', '#38BDF8', '#EC4899'];
    return Object.fromEntries((packContent?.routeProfiles || []).map((profile, index) => [
      profile.id,
      colors[index % colors.length],
    ]));
  }, [packContent]);
  const networkPalette = useMemo(() => {
    const colors = ['#F59E0B', '#38BDF8', '#A78BFA', '#34D399', '#F472B6', '#FB7185'];
    const options = [
      ...infrastructure.map((item) => ({ ...item, catalog: 'infrastructure' })),
      ...connections.map((item) => ({ ...item, catalog: 'connection' })),
    ];
    return Object.fromEntries(options.map((item, index) => [
      networkTypeKey(item.catalog, item.id), colors[index % colors.length],
    ]));
  }, [connections, infrastructure]);
  const conditionPalette = useMemo(() => {
    const colors = ['#F97316', '#EF4444', '#EAB308', '#EC4899'];
    return Object.fromEntries(terrainConditions.map((item, index) => [item.id, colors[index % colors.length]]));
  }, [terrainConditions]);

  const preparationStatus = useMemo(() => {
    if (!calibration?.scale?.metersPerPixel || !calibration?.grid?.enabled) {
      return {
        id: 'empty',
        label: language === 'fr' ? 'Non préparée' : 'Not prepared',
        className: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
      };
    }
    if (!terrainStats.total || terrainStats.ambiguous > 0 || terrainStale || featuresStale) {
      return {
        id: 'partial',
        label: language === 'fr' ? 'À compléter' : 'Needs work',
        className: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
      };
    }
    return {
      id: 'ready',
      label: language === 'fr' ? 'Prête au voyage' : 'Travel ready',
      className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    };
  }, [calibration, featuresStale, language, terrainStale, terrainStats]);

  const setTravelView = useCallback((view, nextMapId = selectedMapId) => {
    setJourneyView(view);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', view);
    if (nextMapId) nextParams.set('mapId', nextMapId);
    else nextParams.delete('mapId');
    setSearchParams(nextParams);
    setInteractionMode('navigate');
  }, [searchParams, selectedMapId, setSearchParams]);

  const openJourney = useCallback((journey) => {
    if (journey.mapId) setSelectedMapId(journey.mapId);
    if (journey.packageId && journey.packageVersion) {
      setSelectedPackKey(`${journey.packageId}@${journey.packageVersion}`);
    }
    setWorkspacePanel(journey.kind === 'run' ? 'engine' : 'route');
    setTravelView(journey.kind === 'run' ? 'run' : 'new', journey.mapId);
  }, [setTravelView]);

  const updateSetupTool = useCallback((tool) => {
    setWorkspacePanel(tool);
    if (tool === 'terrain') setTravelEditLayer('ground');
    if (tool === 'network') setTravelEditLayer('network');
    if (tool === 'condition') setTravelEditLayer('condition');
    setInteractionMode('navigate');
  }, []);

  const setGridVisible = useCallback((enabled) => {
    if (!calibration) return;
    updateGrid({ ...calibration.grid, enabled });
  }, [calibration, updateGrid]);

  const renderLayers = (className = '') => calibration && terrainClassification && mapFeatures ? (
    <div className={className} onPointerDown={(event) => event.stopPropagation()}>
      <TravelLayersPopover
        language={language}
        gridVisible={calibration.grid.enabled}
        onGridVisibleChange={setGridVisible}
        terrainVisible={terrainClassification.overlayEnabled !== false}
        onTerrainVisibleChange={(enabled) => updateTerrainClassification((current) => ({ ...current, overlayEnabled: enabled }))}
        networkVisible={mapFeatures.networkOverlayEnabled !== false}
        onNetworkVisibleChange={(enabled) => updateMapFeatures((current) => ({ ...current, networkOverlayEnabled: enabled }))}
        obstacleVisible={mapFeatures.conditionOverlayEnabled !== false}
        onObstacleVisibleChange={(enabled) => updateMapFeatures((current) => ({ ...current, conditionOverlayEnabled: enabled }))}
        pinsVisible={pinsVisible}
        onPinsVisibleChange={setPinsVisible}
        terrainOpacity={terrainOverlayOpacity}
        onTerrainOpacityChange={setTerrainOverlayOpacity}
      />
    </div>
  ) : null;

  const renderMap = ({ journey = false, immersive = false } = {}) => (
    <section className={`relative min-w-0 flex-1 overflow-hidden bg-[#151719] ${immersive ? 'h-full min-h-0' : 'min-h-[540px]'}`}>
      {activeMap && calibration && !calibrationLoading ? (
        <TravelMapCanvas
          map={activeMap}
          calibration={calibration}
          language={language}
          distanceUnit={distanceUnit}
          interactionMode={interactionMode}
          onGridChange={updateGrid}
          onGridCalibrated={handleGridCalibrated}
          onScaleSegmentMeasured={setMeasuredSegment}
          onInteractionComplete={() => setInteractionMode('navigate')}
          onImageDimensions={handleImageDimensions}
          terrainAssignments={terrainClassification?.assignments || {}}
          terrainPalette={terrainPalette}
          terrainBrushColor={terrainPalette[selectedTerrainId] || accentColor}
          terrainOverlayEnabled={terrainClassification?.overlayEnabled !== false}
          terrainOverlayOpacity={terrainOverlayOpacity / 100}
          selectedTerrainHexes={journey ? new Set() : selectedTerrainHexes}
          brushRadius={brushRadius}
          onTerrainSample={journey ? undefined : handleTerrainSample}
          onTerrainStrokeStart={journey ? undefined : handleTerrainStrokeStart}
          onTerrainPaint={journey ? undefined : handleTerrainPaint}
          onTerrainStrokeEnd={journey ? undefined : handleTerrainStrokeEnd}
          onToggleTerrainHex={journey ? undefined : handleToggleTerrainHex}
          networkSegments={mapFeatures?.networkSegments || {}}
          networkPalette={networkPalette}
          networkOverlayEnabled={mapFeatures?.networkOverlayEnabled !== false}
          conditionAssignments={mapFeatures?.conditions || {}}
          conditionPalette={conditionPalette}
          conditionOverlayEnabled={mapFeatures?.conditionOverlayEnabled !== false}
          onNetworkStrokeStart={journey ? undefined : handleNetworkStrokeStart}
          onNetworkPaint={journey ? undefined : handleNetworkPaint}
          onNetworkStrokeEnd={journey ? undefined : handleNetworkStrokeEnd}
          onConditionPaint={journey ? undefined : handleConditionPaint}
          routeVariants={journey ? routeDraft?.variants || [] : []}
          selectedRouteProfileId={journey ? routeDraft?.selectedProfileId : null}
          routePoints={journey ? routePoints : null}
          avoidedRouteHexKeys={journey ? routeDraft?.avoidedHexKeys || [] : []}
          focusedRouteHexKey={journey && workspacePanel === 'route' ? pendingUnknownRouteHexes[0] : null}
          onRouteHexPick={journey ? handleRouteHexPick : undefined}
          routeProfileColors={routeProfileColors}
          pins={pins}
          pinsVisible={pinsVisible}
          onPinsVisibleChange={setPinsVisible}
          partyAnimation={journey && travelRun ? {
            path: travelRun.routePath,
            progress: animationProgress,
            color: accentColor,
            name: travelRun.party.name,
            transportTags: packContent?.transports?.find(
              (item) => item.id === travelRun.parameters.transportId,
            )?.tags || [],
          } : null}
          fitViewKey={`${toolsPanelCollapsed}:${surface}:${journeyView}`}
          immersive={immersive}
        />
      ) : activeMap && calibrationLoading ? (
        <div className="flex h-full min-h-[540px] items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />{text.loading}
        </div>
      ) : (
        <div className="flex h-full min-h-[520px] flex-col items-center justify-center p-8 text-center">
          <MapIcon className="mb-4 h-10 w-10 text-muted-foreground/50" />
          <h2 className="font-cinzel text-lg font-semibold">{maps.length ? text.mapPlaceholder : text.noMap}</h2>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">{text.noMapHelp}</p>
          <Button asChild variant="outline" className="mt-5">
            <Link to={`/campaigns/${campaignId}/maps`}>{text.openMaps}<ChevronRight /></Link>
          </Button>
        </div>
      )}
      {renderLayers('absolute right-14 top-3 z-30')}
      {calibration && !immersive && (
        <div className="absolute bottom-3 right-3 z-20">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="gap-2 border border-white/10 bg-black/70 text-white shadow-lg backdrop-blur hover:bg-black/85 hover:text-white"
            onClick={() => {
              setToolsPanelCollapsed((collapsed) => !collapsed);
              setInteractionMode('navigate');
            }}
            aria-expanded={!toolsPanelCollapsed}
            aria-label={toolsPanelCollapsed ? text.showTools : text.hideTools}
          >
            {toolsPanelCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            {toolsPanelCollapsed ? text.showTools : text.hideTools}
          </Button>
        </div>
      )}
    </section>
  );

  const renderRoutePlanner = () => routeDraft && !routeLoading && packContent ? (
    <TravelRoutePanel
      language={language}
      distanceUnit={distanceUnit}
      time={packContent.time}
      transports={packContent.transports}
      paces={packContent.paces}
      dangerLevels={packContent.dangerLevels}
      profiles={packContent.routeProfiles}
      terrains={terrains}
      draft={routeDraft}
      onDraftChange={handleRouteDraftChange}
      interactionMode={interactionMode}
      onInteractionModeChange={setInteractionMode}
      startLabel={routePointLabel(routeDraft.startHexKey, routeDraft.startPinId)}
      destinationLabel={routePointLabel(routeDraft.destinationHexKey, routeDraft.destinationPinId)}
      waypointLabels={routeDraft.waypointHexKeys.map((key, index) => (
        routePointLabel(key, routeDraft.waypointPinIds?.[index])
      ))}
      onRemoveWaypoint={handleRemoveRouteWaypoint}
      avoidedLabels={(routeDraft.avoidedHexKeys || []).map((key) => ({ key, label: routeHexLabel(key) }))}
      onRemoveAvoidedHex={handleRemoveAvoidedHex}
      variants={routeDraft.variants}
      onReset={handleResetRoute}
      unknownHexLabel={routeHexLabel(pendingUnknownRouteHexes[0])}
      unknownRemaining={pendingUnknownRouteHexes.length}
      onAssignUnknownTerrain={handleAssignUnknownTerrain}
      onConfirm={handleConfirmRoute}
      saveStatus={routeComputing ? 'saving' : routeSaveStatus}
      stale={routeStale}
      scaleReady={Boolean(calibration?.scale?.metersPerPixel)}
      profileColors={routeProfileColors}
    />
  ) : (
    <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />{text.loading}
    </div>
  );

  const handleDisplayedRunChange = (nextRun) => {
    if (nextRun.currentStepIndex !== travelRun?.currentStepIndex) {
      const positionKey = nextRun.currentStepIndex === 0
        ? nextRun.routePath[0]
        : nextRun.steps[nextRun.currentStepIndex - 1].hexKeys.at(-1);
      const pathIndex = nextRun.routePath.indexOf(positionKey);
      setAnimationPlaying(false);
      setAnimationProgress(Math.max(0, pathIndex) / Math.max(1, nextRun.routePath.length - 1));
    }
    setTravelRun(nextRun);
    setRunStale(false);
    setJourneys((current) => current.map((journey) => (
      journey.kind === 'run'
      && journey.mapId === nextRun.mapId
      && journey.packageId === nextRun.packageId
      && journey.packageVersion === nextRun.packageVersion
        ? { ...journey, status: nextRun.status, currentStepIndex: nextRun.currentStepIndex, stepCount: nextRun.steps.length }
        : journey
    )));
  };

  const renderEngine = () => packContent && routeDraft && terrainClassification && mapFeatures
    && !routeLoading && !runLoading && !terrainLoading && !featuresLoading ? (
      <TravelEnginePanel
        language={language}
        distanceUnit={distanceUnit}
        pack={packContent}
        routeDraft={routeDraft}
        run={travelRun}
        accentColor={accentColor}
        saveStatus={runStale ? 'stale' : runSaveStatus}
        animationProgress={animationProgress}
        animationPlaying={animationPlaying}
        onGenerate={handleGenerateRun}
        onRunChange={handleDisplayedRunChange}
        onRerollEvent={handleRerollEvent}
        onAnimationProgressChange={setAnimationProgress}
        onAnimationPlayingChange={setAnimationPlaying}
      />
    ) : (
      <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />{text.loading}
      </div>
    );

  if (loading || !currentCampaign || currentCampaign.id !== campaignId) {
    return (
      <MainLayout fluid>
        <div className="flex flex-1 items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{text.loading}</span>
        </div>
      </MainLayout>
    );
  }

  if (!isMJ) {
    return (
      <MainLayout>
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>{text.title}</AlertTitle>
          <AlertDescription>{text.forbidden}</AlertDescription>
        </Alert>
      </MainLayout>
    );
  }

  if (surface === 'map-setup') {
    const setupLabels = {
      calibration: language === 'fr' ? 'Calage' : 'Alignment',
      terrain: language === 'fr' ? 'Terrains' : 'Terrains',
      network: language === 'fr' ? 'Réseaux' : 'Networks',
      condition: language === 'fr' ? 'Obstacles' : 'Obstacles',
      places: language === 'fr' ? 'Lieux' : 'Places',
    };
    const showTerrainTools = terrainClassification && mapFeatures && !terrainLoading && !featuresLoading;

    return (
      <MainLayout fluid>
        <div className="flex min-h-0 flex-1 flex-col bg-background" data-testid="travel-map-setup-page">
          <header className="flex min-h-[66px] flex-wrap items-center gap-3 border-b border-border bg-[#0b141d]/95 px-4 py-2 text-slate-100 md:px-5">
            <div className="min-w-[220px] flex-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-400">
                <Link to={`/campaigns/${campaignId}/maps`} className="hover:text-white">{language === 'fr' ? 'Cartes' : 'Maps'}</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="max-w-[220px] truncate">{activeMap?.name || text.noMap}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 md:absolute md:left-1/2 md:-translate-x-1/2">
              <Crosshair className="hidden h-5 w-5 text-slate-300 sm:block" />
              <h1 className="font-cinzel text-lg font-bold tracking-wide md:text-xl">{language === 'fr' ? 'Paramétrage Voyage' : 'Travel setup'}</h1>
            </div>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <Badge variant="outline" className="border-violet-500/35 bg-violet-500/10 text-violet-200">
                {selectedPack?.name || (language === 'fr' ? 'Univers' : 'Universe')}
              </Badge>
              <Badge variant="outline" className={preparationStatus.className}>{preparationStatus.label}</Badge>
              <Button
                size="sm"
                disabled={!activeMap || !selectedPack}
                onClick={async () => {
                  await saveSettings();
                  navigate(`/campaigns/${campaignId}/travel?view=new&mapId=${activeMap.id}`);
                }}
              >
                <Plus className="h-4 w-4" />{language === 'fr' ? 'Créer un voyage' : 'Create journey'}
              </Button>
            </div>
          </header>

          {error && (
            <div className="border-b border-border px-4 py-2">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{text.loadError}</AlertTitle>
                <AlertDescription>{typeof error === 'string' ? error : text.loadError}</AlertDescription>
              </Alert>
            </div>
          )}

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <TravelSetupRail language={language} active={workspacePanel} onChange={updateSetupTool} />

            {!toolsPanelCollapsed && (
              <aside className="relative w-[304px] shrink-0 overflow-y-auto border-r border-border bg-card/95" data-testid="travel-map-setup-inspector">
                <div className="sticky top-0 z-10 flex h-12 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur">
                  <h2 className="font-cinzel text-sm font-bold">{setupLabels[workspacePanel]}</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setToolsPanelCollapsed(true);
                      setInteractionMode('navigate');
                    }}
                    aria-label={`${text.hideTools} · ${setupLabels[workspacePanel]}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </div>

                <div className="p-3">
                  {workspacePanel === 'calibration' && calibration && (
                    <TravelCalibrationPanel
                      calibration={calibration}
                      language={language}
                      distanceUnit={distanceUnit}
                      universeDefaultMetersPerHex={recommendedMeters}
                      interactionMode={interactionMode}
                      onInteractionModeChange={(mode) => {
                        setInteractionMode(mode);
                        if (mode !== 'navigate') setMeasuredSegment(null);
                      }}
                      measuredSegment={measuredSegment}
                      onMeasuredSegmentClear={() => setMeasuredSegment(null)}
                      onCalibrationChange={updateCalibration}
                      saveStatus={calibrationSaveStatus}
                    />
                  )}

                  {workspacePanel === 'terrain' && (showTerrainTools ? (
                    <TravelTerrainPanel
                      language={language}
                      terrains={terrains}
                      samples={terrainClassification.samples}
                      selectedTerrainId={selectedTerrainId}
                      onSelectedTerrainChange={setSelectedTerrainId}
                      interactionMode={interactionMode}
                      onInteractionModeChange={setInteractionMode}
                      brushRadius={brushRadius}
                      onBrushRadiusChange={setBrushRadius}
                      analysisStatus={analysisStatus}
                      analysisProgress={analysisProgress}
                      analyzedHexCount={analyzedHexCount}
                      onAnalyze={handleAnalyzeTerrain}
                      onReclassify={handleReclassifyTerrain}
                      onRemoveSample={handleRemoveTerrainSample}
                      stats={terrainStats}
                      selectedCount={selectedTerrainHexes.size}
                      onApplySelection={handleApplyTerrainSelection}
                      onClearSelection={() => setSelectedTerrainHexes(new Set())}
                      overlayEnabled={terrainClassification.overlayEnabled !== false}
                      onOverlayEnabledChange={(enabled) => updateTerrainClassification((current) => ({ ...current, overlayEnabled: enabled }))}
                      saveStatus={terrainSaveStatus}
                      stale={terrainStale}
                      canUndo={terrainUndoStack.length > 0}
                      canRedo={terrainRedoStack.length > 0}
                      onUndo={handleTerrainUndo}
                      onRedo={handleTerrainRedo}
                    />
                  ) : <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{text.loading}</div>)}

                  {(workspacePanel === 'network' || workspacePanel === 'condition') && (showTerrainTools ? (
                    <TravelMapFeaturesPanel
                      language={language}
                      layer={workspacePanel}
                      infrastructure={infrastructure}
                      connections={connections}
                      conditions={terrainConditions}
                      selectedNetworkType={selectedNetworkType}
                      onSelectedNetworkTypeChange={setSelectedNetworkType}
                      selectedConditionId={selectedConditionId}
                      onSelectedConditionChange={setSelectedConditionId}
                      interactionMode={interactionMode}
                      onInteractionModeChange={setInteractionMode}
                      networkSegmentCount={Object.keys(mapFeatures.networkSegments).length}
                      conditionHexCount={Object.keys(mapFeatures.conditions).length}
                      overlayEnabled={workspacePanel === 'network'
                        ? mapFeatures.networkOverlayEnabled !== false
                        : mapFeatures.conditionOverlayEnabled !== false}
                      onOverlayEnabledChange={(enabled) => updateMapFeatures((current) => ({
                        ...current,
                        [workspacePanel === 'network' ? 'networkOverlayEnabled' : 'conditionOverlayEnabled']: enabled,
                      }))}
                      saveStatus={featuresSaveStatus}
                      stale={featuresStale}
                      canUndo={featureUndoStack.length > 0}
                      canRedo={featureRedoStack.length > 0}
                      onUndo={handleFeatureUndo}
                      onRedo={handleFeatureRedo}
                    />
                  ) : <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{text.loading}</div>)}

                  {workspacePanel === 'places' && (
                    <TravelPlacesPanel
                      language={language}
                      pins={pins}
                      pinsVisible={pinsVisible}
                      onPinsVisibleChange={setPinsVisible}
                      onOpenMaps={() => navigate(`/campaigns/${campaignId}/maps?mapId=${activeMap?.id || ''}`)}
                    />
                  )}
                </div>
              </aside>
            )}

            {renderMap()}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (surface === 'travel') {
    const activeSection = journeyView === 'run' && !travelRun ? 'new' : journeyView;
    const planningStage = workspacePanel === 'engine' && routeDraft?.status === 'confirmed' ? 'engine' : 'route';

    if (activeSection === 'run' && travelRun && packContent) {
      return (
        <MainLayout fluid>
          <TravelRunCommandCenter
            language={language}
            distanceUnit={distanceUnit}
            pack={packContent}
            run={travelRun}
            accentColor={accentColor}
            animationProgress={animationProgress}
            animationPlaying={animationPlaying}
            map={renderMap({ journey: true, immersive: true })}
            onOpenJourneys={() => setTravelView('list')}
            onModify={() => {
              setWorkspacePanel('route');
              setTravelView('new');
            }}
            onRunChange={handleDisplayedRunChange}
            onRerollEvent={handleRerollEvent}
            onAnimationProgressChange={setAnimationProgress}
            onAnimationPlayingChange={setAnimationPlaying}
          />
        </MainLayout>
      );
    }

    return (
      <MainLayout fluid>
        <div className="flex min-h-0 flex-1 flex-col bg-background" data-testid="travel-journeys-page">
          <header className="border-b border-border bg-card/75 px-4 py-3 backdrop-blur md:px-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex min-w-[220px] items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border" style={{ color: accentColor, borderColor: `${accentColor}66`, backgroundColor: `${accentColor}12` }}>
                  <Navigation className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="font-cinzel text-xl font-bold">{text.title}</h1>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Préparer et jouer les expéditions du groupe' : 'Plan and play the party expeditions'}</p>
                </div>
              </div>

              <nav className="flex flex-1 items-center justify-center gap-1" aria-label={language === 'fr' ? 'Sections Voyage' : 'Travel sections'}>
                {[
                  ['list', List, language === 'fr' ? 'Voyages' : 'Journeys'],
                  ['new', Sparkles, language === 'fr' ? 'Nouveau voyage' : 'New journey'],
                  ...(travelRun ? [['run', Play, language === 'fr' ? 'Voyage en cours' : 'Active journey']] : []),
                ].map(([id, Icon, label]) => (
                  <Button key={id} variant={activeSection === id ? 'secondary' : 'ghost'} size="sm" onClick={() => {
                    if (id === 'new') setWorkspacePanel('route');
                    if (id === 'run') setWorkspacePanel('engine');
                    setTravelView(id);
                  }}>
                    <Icon className="h-4 w-4" />{label}
                  </Button>
                ))}
              </nav>

              <Button size="sm" onClick={() => {
                setWorkspacePanel('route');
                setTravelView('new');
              }}><Plus className="h-4 w-4" />{language === 'fr' ? 'Nouveau voyage' : 'New journey'}</Button>
            </div>
          </header>

          {error && (
            <div className="px-5 pt-4 md:px-7">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{text.loadError}</AlertTitle>
                <AlertDescription>{typeof error === 'string' ? error : text.loadError}</AlertDescription>
              </Alert>
            </div>
          )}

          {activeSection === 'list' ? (
            <TravelJourneyLibrary
              language={language}
              journeys={journeys}
              onOpenJourney={openJourney}
              onCreateJourney={() => {
                setWorkspacePanel('route');
                setTravelView('new');
              }}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/20 px-4 py-2">
                <div className="min-w-[220px]">
                  <Select value={selectedMapId} onValueChange={(value) => {
                    setSelectedMapId(value);
                    setTravelView(activeSection, value);
                  }} disabled={!maps.length || activeSection === 'run'}>
                    <SelectTrigger className="h-8" aria-label={text.map}><SelectValue placeholder={text.noMap} /></SelectTrigger>
                    <SelectContent>{maps.map((map) => <SelectItem key={map.id} value={map.id}>{map.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Badge variant="outline" className="border-violet-500/25 bg-violet-500/5 text-violet-700 dark:text-violet-300">{selectedPack?.name}</Badge>
                <Badge variant="outline" className={preparationStatus.className}>{preparationStatus.label}</Badge>
                <div className="ml-auto flex items-center gap-2">
                  <div className="flex h-8 rounded-md border border-input p-0.5">
                    {['km', 'mi'].map((unit) => (
                      <button key={unit} type="button" onClick={() => switchDistanceUnit(unit)} className={`rounded px-2.5 text-[11px] font-semibold ${distanceUnit === unit ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>{unit}</button>
                    ))}
                  </div>
                  {activeMap && (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/campaigns/${campaignId}/maps/${activeMap.id}/travel-setup`}><MapIcon className="h-4 w-4" />{language === 'fr' ? 'Paramétrer la carte' : 'Set up map'}</Link>
                    </Button>
                  )}
                </div>
              </div>

              <div className={`grid min-h-0 flex-1 transition-[grid-template-columns] duration-300 ${toolsPanelCollapsed ? 'grid-cols-[minmax(0,1fr)]' : 'xl:grid-cols-[minmax(0,1fr)_390px]'}`}>
                {renderMap({ journey: true })}

                {!toolsPanelCollapsed && (
                  <aside className="min-h-0 overflow-y-auto border-l border-border bg-card/65 p-3">
                    {activeSection === 'new' && (
                      <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg border bg-muted/25 p-1">
                        <Button variant={planningStage === 'route' ? 'secondary' : 'ghost'} size="sm" onClick={() => setWorkspacePanel('route')}>
                          <Route className="h-4 w-4" />{language === 'fr' ? 'Itinéraire' : 'Route'}
                        </Button>
                        <Button variant={planningStage === 'engine' ? 'secondary' : 'ghost'} size="sm" disabled={routeDraft?.status !== 'confirmed'} onClick={() => setWorkspacePanel('engine')}>
                          <Sparkles className="h-4 w-4" />{language === 'fr' ? 'Groupe et départ' : 'Party & start'}
                        </Button>
                      </div>
                    )}

                    {activeSection === 'new'
                      ? (planningStage === 'route' ? renderRoutePlanner() : renderEngine())
                      : (travelRun ? renderEngine() : (
                        <div className="flex min-h-[420px] flex-col items-center justify-center p-6 text-center">
                          <AlertTriangle className="h-9 w-9 text-amber-500" />
                          <h2 className="mt-3 font-cinzel text-base font-semibold">{text.noJourney}</h2>
                          <p className="mt-2 text-sm text-muted-foreground">{text.noJourneyHelp}</p>
                          <Button className="mt-4" onClick={() => setTravelView('new')}><Sparkles className="h-4 w-4" />{language === 'fr' ? 'Préparer un voyage' : 'Plan a journey'}</Button>
                        </div>
                      ))}
                  </aside>
                )}
              </div>
            </div>
          )}
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout fluid>
      <div className="flex min-h-0 flex-1 flex-col bg-background">
        <header className="border-b border-border bg-card/70 px-5 py-4 backdrop-blur md:px-7">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border"
                  style={{ color: accentColor, borderColor: `${accentColor}66`, backgroundColor: `${accentColor}12` }}
                >
                  <Navigation className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="font-cinzel text-2xl font-bold tracking-tight">{text.title}</h1>
                  <p className="max-w-3xl text-sm text-muted-foreground">{text.subtitle}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[210px] space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {text.universe}
                </label>
                <Select value={selectedPackKey} onValueChange={setSelectedPackKey}>
                  <SelectTrigger aria-label={text.universe}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {packs.map((pack) => (
                      <SelectItem key={pack.id} value={pack.id}>
                        {pack.name} · {pack.version}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[210px] space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {text.map}
                </label>
                <Select value={selectedMapId} onValueChange={setSelectedMapId} disabled={!maps.length}>
                  <SelectTrigger aria-label={text.map}><SelectValue placeholder={text.noMap} /></SelectTrigger>
                  <SelectContent>
                    {maps.map((map) => <SelectItem key={map.id} value={map.id}>{map.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {text.units}
                </span>
                <div className="flex h-9 rounded-md border border-input p-0.5">
                  {['km', 'mi'].map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      onClick={() => switchDistanceUnit(unit)}
                      className={`rounded px-3 text-xs font-semibold transition-colors ${distanceUnit === unit ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      aria-pressed={distanceUnit === unit}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={saveSettings} disabled={!selectedPack || !selectedMapId || saving}>
                {saving ? <Loader2 className="animate-spin" /> : isConfigured ? <Check /> : <Save />}
                {text.save}
              </Button>
            </div>
          </div>
        </header>

        {error && (
          <div className="px-5 pt-4 md:px-7">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{text.loadError}</AlertTitle>
              <AlertDescription>{typeof error === 'string' ? error : text.loadError}</AlertDescription>
            </Alert>
          </div>
        )}

        <div className={`grid min-h-0 flex-1 gap-4 p-4 transition-[grid-template-columns] duration-300 md:p-5 ${toolsPanelCollapsed ? 'xl:grid-cols-[minmax(0,1fr)]' : 'xl:grid-cols-[minmax(0,1fr)_380px]'}`}>
          <section className="relative min-h-[540px] overflow-hidden rounded-xl border border-border bg-muted/20">
            {activeMap && calibration && !calibrationLoading ? (
              <TravelMapCanvas
                map={activeMap}
                calibration={calibration}
                language={language}
                distanceUnit={distanceUnit}
                interactionMode={interactionMode}
                onGridChange={updateGrid}
                onGridCalibrated={handleGridCalibrated}
                onScaleSegmentMeasured={setMeasuredSegment}
                onInteractionComplete={() => setInteractionMode('navigate')}
                onImageDimensions={handleImageDimensions}
                terrainAssignments={terrainClassification?.assignments || {}}
                terrainPalette={terrainPalette}
                terrainBrushColor={terrainPalette[selectedTerrainId] || accentColor}
                terrainOverlayEnabled={terrainClassification?.overlayEnabled !== false}
                selectedTerrainHexes={selectedTerrainHexes}
                brushRadius={brushRadius}
                onTerrainSample={handleTerrainSample}
                onTerrainStrokeStart={handleTerrainStrokeStart}
                onTerrainPaint={handleTerrainPaint}
                onTerrainStrokeEnd={handleTerrainStrokeEnd}
                onToggleTerrainHex={handleToggleTerrainHex}
                networkSegments={mapFeatures?.networkSegments || {}}
                networkPalette={networkPalette}
                networkOverlayEnabled={mapFeatures?.networkOverlayEnabled !== false}
                conditionAssignments={mapFeatures?.conditions || {}}
                conditionPalette={conditionPalette}
                conditionOverlayEnabled={mapFeatures?.conditionOverlayEnabled !== false}
                onNetworkStrokeStart={handleNetworkStrokeStart}
                onNetworkPaint={handleNetworkPaint}
                onNetworkStrokeEnd={handleNetworkStrokeEnd}
                onConditionPaint={handleConditionPaint}
                routeVariants={routeDraft?.variants || []}
                selectedRouteProfileId={routeDraft?.selectedProfileId}
                routePoints={routePoints}
                avoidedRouteHexKeys={routeDraft?.avoidedHexKeys || []}
                focusedRouteHexKey={workspacePanel === 'route' ? pendingUnknownRouteHexes[0] : null}
                onRouteHexPick={handleRouteHexPick}
                routeProfileColors={routeProfileColors}
                pins={pins}
                pinsVisible={pinsVisible}
                onPinsVisibleChange={setPinsVisible}
                partyAnimation={travelRun ? {
                  path: travelRun.routePath,
                  progress: animationProgress,
                  color: accentColor,
                  name: travelRun.party.name,
                  transportTags: packContent?.transports?.find(
                    (item) => item.id === travelRun.parameters.transportId,
                  )?.tags || [],
                } : null}
                fitViewKey={toolsPanelCollapsed}
              />
            ) : activeMap && calibrationLoading ? (
              <div className="flex h-full min-h-[540px] items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />{text.loading}
              </div>
            ) : (
              <div className="flex h-full min-h-[520px] flex-col items-center justify-center p-8 text-center">
                <MapIcon className="mb-4 h-10 w-10 text-muted-foreground/50" />
                <h2 className="font-cinzel text-lg font-semibold">{maps.length ? text.mapPlaceholder : text.noMap}</h2>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">{text.noMapHelp}</p>
                <Button asChild variant="outline" className="mt-5">
                  <Link to={`/campaigns/${campaignId}/maps`}>{text.openMaps}<ChevronRight /></Link>
                </Button>
              </div>
            )}

            {calibration && (
              <div className="absolute bottom-3 right-3 z-20">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="gap-2 border border-white/10 bg-black/70 text-white shadow-lg backdrop-blur hover:bg-black/85 hover:text-white"
                  onClick={() => {
                    setToolsPanelCollapsed((collapsed) => !collapsed);
                    setInteractionMode('navigate');
                  }}
                  aria-expanded={!toolsPanelCollapsed}
                  aria-label={toolsPanelCollapsed ? text.showTools : text.hideTools}
                >
                  {toolsPanelCollapsed ? <PanelRightOpen className="h-4 w-4" /> : <PanelRightClose className="h-4 w-4" />}
                  {toolsPanelCollapsed ? text.showTools : text.hideTools}
                </Button>
              </div>
            )}
          </section>

          {!toolsPanelCollapsed && <aside className="flex min-h-0 flex-col gap-4 overflow-auto">
            {calibration && (
              <Tabs value={workspacePanel} onValueChange={(panel) => {
                setWorkspacePanel(panel);
                setInteractionMode('navigate');
              }}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="calibration">{text.calibrationTab}</TabsTrigger>
                  <TabsTrigger value="terrain">{text.terrainTab}</TabsTrigger>
                  <TabsTrigger value="route">{text.routeTab}</TabsTrigger>
                  <TabsTrigger value="engine" className="font-semibold data-[state=active]:text-primary">{text.engineTab}</TabsTrigger>
                </TabsList>
                <TabsContent value="calibration" className="mt-3">
                  <TravelCalibrationPanel
                    calibration={calibration}
                    language={language}
                    distanceUnit={distanceUnit}
                    universeDefaultMetersPerHex={recommendedMeters}
                    interactionMode={interactionMode}
                    onInteractionModeChange={(mode) => {
                      setInteractionMode(mode);
                      if (mode !== 'navigate') setMeasuredSegment(null);
                    }}
                    measuredSegment={measuredSegment}
                    onMeasuredSegmentClear={() => setMeasuredSegment(null)}
                    onCalibrationChange={updateCalibration}
                    saveStatus={calibrationSaveStatus}
                  />
                </TabsContent>
                <TabsContent value="terrain" className="mt-3">
                  {terrainClassification && mapFeatures && !terrainLoading && !featuresLoading ? (
                    <div className="space-y-3">
                      <TravelLayerSwitcher
                        language={language}
                        value={travelEditLayer}
                        onChange={(layer) => {
                          setTravelEditLayer(layer);
                          setInteractionMode('navigate');
                        }}
                      />
                      {travelEditLayer === 'ground' ? (
                        <TravelTerrainPanel
                          language={language}
                          terrains={terrains}
                          samples={terrainClassification.samples}
                          selectedTerrainId={selectedTerrainId}
                          onSelectedTerrainChange={setSelectedTerrainId}
                          interactionMode={interactionMode}
                          onInteractionModeChange={setInteractionMode}
                          brushRadius={brushRadius}
                          onBrushRadiusChange={setBrushRadius}
                          analysisStatus={analysisStatus}
                          analysisProgress={analysisProgress}
                          analyzedHexCount={analyzedHexCount}
                          onAnalyze={handleAnalyzeTerrain}
                          onReclassify={handleReclassifyTerrain}
                          onRemoveSample={handleRemoveTerrainSample}
                          stats={terrainStats}
                          selectedCount={selectedTerrainHexes.size}
                          onApplySelection={handleApplyTerrainSelection}
                          onClearSelection={() => setSelectedTerrainHexes(new Set())}
                          overlayEnabled={terrainClassification.overlayEnabled !== false}
                          onOverlayEnabledChange={(overlayEnabled) => updateTerrainClassification((current) => ({ ...current, overlayEnabled }))}
                          saveStatus={terrainSaveStatus}
                          stale={terrainStale}
                          canUndo={terrainUndoStack.length > 0}
                          canRedo={terrainRedoStack.length > 0}
                          onUndo={handleTerrainUndo}
                          onRedo={handleTerrainRedo}
                        />
                      ) : (
                        <TravelMapFeaturesPanel
                          language={language}
                          layer={travelEditLayer}
                          infrastructure={infrastructure}
                          connections={connections}
                          conditions={terrainConditions}
                          selectedNetworkType={selectedNetworkType}
                          onSelectedNetworkTypeChange={setSelectedNetworkType}
                          selectedConditionId={selectedConditionId}
                          onSelectedConditionChange={setSelectedConditionId}
                          interactionMode={interactionMode}
                          onInteractionModeChange={setInteractionMode}
                          networkSegmentCount={Object.keys(mapFeatures.networkSegments).length}
                          conditionHexCount={Object.keys(mapFeatures.conditions).length}
                          overlayEnabled={travelEditLayer === 'network'
                            ? mapFeatures.networkOverlayEnabled !== false
                            : mapFeatures.conditionOverlayEnabled !== false}
                          onOverlayEnabledChange={(enabled) => updateMapFeatures((current) => ({
                            ...current,
                            [travelEditLayer === 'network' ? 'networkOverlayEnabled' : 'conditionOverlayEnabled']: enabled,
                          }))}
                          saveStatus={featuresSaveStatus}
                          stale={featuresStale}
                          canUndo={featureUndoStack.length > 0}
                          canRedo={featureRedoStack.length > 0}
                          onUndo={handleFeatureUndo}
                          onRedo={handleFeatureRedo}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />{text.loading}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="route" className="mt-3">
                  {routeDraft && !routeLoading && packContent ? (
                    <TravelRoutePanel
                      language={language}
                      distanceUnit={distanceUnit}
                      time={packContent.time}
                      transports={packContent.transports}
                      paces={packContent.paces}
                      dangerLevels={packContent.dangerLevels}
                      profiles={packContent.routeProfiles}
                      terrains={terrains}
                      draft={routeDraft}
                      onDraftChange={handleRouteDraftChange}
                      interactionMode={interactionMode}
                      onInteractionModeChange={setInteractionMode}
                      startLabel={routePointLabel(routeDraft.startHexKey, routeDraft.startPinId)}
                      destinationLabel={routePointLabel(routeDraft.destinationHexKey, routeDraft.destinationPinId)}
                      waypointLabels={routeDraft.waypointHexKeys.map((key, index) => (
                        routePointLabel(key, routeDraft.waypointPinIds?.[index])
                      ))}
                      onRemoveWaypoint={handleRemoveRouteWaypoint}
                      avoidedLabels={(routeDraft.avoidedHexKeys || []).map((key) => ({ key, label: routeHexLabel(key) }))}
                      onRemoveAvoidedHex={handleRemoveAvoidedHex}
                      variants={routeDraft.variants}
                      onReset={handleResetRoute}
                      unknownHexLabel={routeHexLabel(pendingUnknownRouteHexes[0])}
                      unknownRemaining={pendingUnknownRouteHexes.length}
                      onAssignUnknownTerrain={handleAssignUnknownTerrain}
                      onConfirm={handleConfirmRoute}
                      saveStatus={routeComputing ? 'saving' : routeSaveStatus}
                      stale={routeStale}
                      scaleReady={Boolean(calibration.scale.metersPerPixel)}
                      profileColors={routeProfileColors}
                    />
                  ) : (
                    <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />{text.loading}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="engine" className="mt-3">
                  {packContent && routeDraft && terrainClassification && mapFeatures
                    && !routeLoading && !runLoading && !terrainLoading && !featuresLoading ? (
                    <TravelEnginePanel
                      language={language}
                      distanceUnit={distanceUnit}
                      pack={packContent}
                      routeDraft={routeDraft}
                      run={travelRun}
                      accentColor={accentColor}
                      saveStatus={runStale ? 'stale' : runSaveStatus}
                      animationProgress={animationProgress}
                      animationPlaying={animationPlaying}
                      onGenerate={handleGenerateRun}
                      onRunChange={(nextRun) => {
                        if (nextRun.currentStepIndex !== travelRun?.currentStepIndex) {
                          const positionKey = nextRun.currentStepIndex === 0
                            ? nextRun.routePath[0]
                            : nextRun.steps[nextRun.currentStepIndex - 1].hexKeys.at(-1);
                          const pathIndex = nextRun.routePath.indexOf(positionKey);
                          setAnimationPlaying(false);
                          setAnimationProgress(Math.max(0, pathIndex) / Math.max(1, nextRun.routePath.length - 1));
                        }
                        setTravelRun(nextRun);
                        setRunStale(false);
                      }}
                      onRerollEvent={handleRerollEvent}
                      onAnimationProgressChange={setAnimationProgress}
                      onAnimationPlayingChange={setAnimationPlaying}
                    />
                  ) : (
                    <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />{text.loading}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}

          </aside>}
        </div>
      </div>
    </MainLayout>
  );
}
